/* v17.6: transient UI ownership, initialised before boot-reachable renderers. */
const _stkMenus = { role: null, engagement: null };
let _actionLabelContext = null; // modal-only; never stored
/* ══ v14 (W4b): THE EMPTY SEED — what boot runs on until the parts arrive ═══════════════════
   The data used to be assigned by a <script src> that ran BEFORE this script, so every
   module-level statement below could read `window.__DB__` synchronously and did. Parts are
   fetched, which is asynchronous, so something valid has to stand here in the meantime or the
   whole module-level pass (the _normProgrammes call, the stakeholder-id initialiser, the boot
   gate) would run against undefined.

   An EMPTY portfolio is exactly the right stand-in rather than a special case: the boot gate has
   been "fresh-start friendly" since v5 — a valid object with a projects ARRAY counts as a clean
   load even when empty — and it is also the genuine first-run state, because v14 ships no data
   file and a partless folder is supposed to boot empty and be greeted by name (the migration was
   dropped 2026-08-03: there are no pre-14 schemas).

   ⚠ The guard matters. `window.__DB__` is normally absent here, but the QA harness injects one
   before this script runs so a check can stage a portfolio without a launcher — overwriting that
   would make every such check test the empty seed instead of its own fixture. */
/* ⚠ v14 (W8, ◆3): RECORD WHETHER A PORTFOLIO WAS HANDED TO US, before the seed hides the answer.
   The viewer exists for a page that has NOWHERE to read a portfolio from. Being on file:// is not
   the same thing — the QA battery stages almost every page on file:// and injects its fixture
   here, and keying the viewer on the protocol alone put ten of those checks into read-only mode
   and turned Save off underneath them. What actually distinguishes the ◆3 predicament is that
   nothing outside the app supplied any data at all. */
const _dbWasSupplied = !!(window.__DB__ && typeof window.__DB__ === 'object');
if (!_dbWasSupplied) {
  window.__DB__ = { schemaVersion: SCHEMA_VERSION, programmes: [], projects: [] };
}
/* ══ v14 (W6): THE BRAND MARK — ONE value selects it, which IS the reversion he asked for ═════
   *"can we please retain the other logo in the code, so we can switch back to the current logo if
   needed later."* Both marks live in the <defs> block at the top of <body>; this chooses which one
   the two <use> sites point at. Change `BRAND_MARK` to 'taMark' and the whole app reverts to the
   swoosh-and-sparkle v13.1 shipped.
   ⚠ DEVELOPER-FACING, NOT A USER SETTING, deliberately: there is no settings screen in this app,
   the nav is a closed set of six areas, and he asked for a reversion CAPABILITY rather than a
   preference.
   ⚠ The viewBox travels with the mark, because the two are not the same shape (1:1 against
   216×190) — selecting a mark without its viewBox would letterbox or crop it. */
const BRAND_MARKS = {
  fdMark: { viewBox: '0 0 120 120', label: 'TrendAI FlightDeck' },
  taMark: { viewBox: '0 0 216 190', label: 'TrendAI' }
};
const BRAND_MARK = 'fdMark';
function _applyBrandMark() {
  const spec = BRAND_MARKS[BRAND_MARK] || BRAND_MARKS.fdMark;
  document.querySelectorAll('svg.brand-mark').forEach(svg => {
    svg.setAttribute('viewBox', spec.viewBox);
    svg.setAttribute('aria-label', spec.label);
    const u = svg.querySelector('use');
    if (u) u.setAttribute('href', '#' + BRAND_MARK);
  });
}
function toggleTheme() {
  const isDark = document.documentElement.classList.toggle('dark');
  try { localStorage.setItem('trendai-theme', isDark ? 'dark' : 'light'); } catch(e){}
}
let ACTIONS = [];

/* ── SESSION DATA ── */
/* ── APP META + DELETIONS ── */
let appMeta = {"projectManager":"","lastSaved":""};
let deletedIds = new Set();
/* v17.5 P7: declared before the early dashboard render. Late file/viewer state is consulted only
   after _progressControllerReady is enabled beside the final boot call. Draft and help state are
   session-only and never enter appMeta/uiState/localStorage. */
let _progressControllerReady = false;
let _projectProgressEditor = null;
let _projectProgressApplying = false;
let _progressHelpTrigger = null;
let _progressHelpHideTimer = null;
let _progressHelpBound = false;
let _progressHelpPinned = false;
let _progressHelpDismissedTrigger = null;
/* v17.5 P5: the WBS reorder UI joins P4's shared transient controller only after all
   late file/viewer state exists. The save-date snapshot is early because header rendering
   runs during the first module pass. Neither value is persisted. */
let _wbsReorderReady = false;
let _wbsReorderBound = false;
let _saveDateView = null;   /* {before:string|undefined,confirmed:string|null} while Save is active */

function formatDateDisplay(iso) {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const _LAST_SAVED_TITLE = 'Latest save in the portfolio data loaded in this tab. It may include changes to other projects.';
function _lastSavedStampForDisplay() {
  if (_saveDateView) return _saveDateView.confirmed !== null
    ? _saveDateView.confirmed : _saveDateView.before;
  const db = window.__DB__;
  return db && db.savedAt;
}
function _lastSavedDateText() {
  const stamp = _lastSavedStampForDisplay();
  if (typeof stamp !== 'string') return '—';
  const m = stamp.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '—';
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  const check = new Date(Date.UTC(y, mo - 1, d));
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) return '—';
  return formatDateDisplay(m[0]);
}
function _lastSavedSpanHtml(id) {
  return `<span id="${id}" title="${_escapeHtml(_LAST_SAVED_TITLE)}">${_escapeHtml(_lastSavedDateText())}</span>`;
}
function _repaintLastSavedDates() {
  const text = _lastSavedDateText();
  ['lastSavedDisplay','portfolioLastSavedDisplay','programmeLastSavedDisplay'].forEach(function (id) {
    const el = document.getElementById(id);
    if (!el) return;
    el.textContent = text;
    el.title = _LAST_SAVED_TITLE;
  });
}

function renderHeaderMeta() {
  const pmEl = document.getElementById('pmDisplay');
  const savedEl = document.getElementById('lastSavedDisplay');
  /* v13.1 (A26): empty renders as the placeholder; the placeholder is never stored */
  const _pm = appMeta.projectManager || '—';
  if (pmEl && pmEl.textContent !== _pm) pmEl.textContent = _pm;
  if (savedEl) _repaintLastSavedDates();
}

/* ── v12.3 (W2): ONE manager editor, two record types ────────────────────────────
   A programme had no manager anywhere, which is a real PgMP gap: the person accountable for the
   programme is exactly the sort of fact its own header should carry. Rather than copy the
   project's editor, it is GENERALISED with a `kind` - build standard 2, the same move v12.1 made
   for _inlineRename and toggleCardMenu. handlePmEdit stays as the project's entry point so the
   static markup and every existing caller are untouched.
   The v2.90 behaviour is preserved exactly for both kinds: blur without typing is a no-op, and
   the '—' placeholder is treated as equivalent to empty so merely focusing the field cannot
   dirty Save.
   ⚠ The programme key is LAZY: an empty value DELETES it, so a programme that was never given a
   manager serialises byte-identically (the archived / allocationPct / plannedStart precedent). */
function _managerEdit(el, kind) {
  const name = (el.textContent || '').trim();
  const newValue = name || '—';
  if (kind === 'programme') {
    const P = _activeProg();
    if (!P) return;
    const currentStored = P.manager || '—';
    if (newValue === currentStored) { el.textContent = newValue; return; }
    if (newValue === '—') delete P.manager;   /* lazy: cleared means absent, never '—' on disk */
    else P.manager = newValue;
    el.textContent = newValue;
    markDirty();
    return;
  }
  const currentStored = appMeta.projectManager || '—';
  if (newValue === currentStored) {
    el.textContent = newValue;  /* still normalize display */
    return;
  }
  /* v13.1 (A26): store the CLEARED state as empty, never as the em-dash placeholder. The
     programme branch above already gets this right (it deletes the key); this one wrote
     '—' straight into appMeta.projectManager, so a project whose PM had been cleared had a
     manager literally named "—" on disk, which then read back as a real name everywhere
     that shows one. The dash is display, not data. Census: no project on disk carries it. */
  appMeta.projectManager = (newValue === '—') ? '' : newValue;
  el.textContent = appMeta.projectManager || '—';
  markDirty();
}
function handlePmEdit(el) { _managerEdit(el, 'project'); }
function handleProgrammeManagerEdit(el) { _managerEdit(el, 'programme'); }

/* ── v13.1 (A07): the header name is the most prominent editable text on the project page,
   and until now the edit was thrown away. This handler never touched projects[].name: it
   rewrote the DOM node and stopped, so the new name survived on screen until the next
   project switch or reload and then reverted. The dirty guard was independently broken in
   the same six lines — it compared the TRIMMED value against the LIVE DOM text, so any
   rename without surrounding whitespace matched and returned before markDirty().
   It now uses the real rename path, the same one the breadcrumb and the overview row use,
   and the same shape handleProgrammeNameEdit has one level up: renameProject() owns the
   store, the repaint (header, breadcrumb, document title) and the dirty flag. It REFUSES an
   empty, whitespace-only or unchanged name — and on a refusal we put the stored name back,
   so the header can never keep showing a name the portfolio does not hold. */
function handleProjectEdit(el) {
  const db = window.__DB__;
  const cur = (db && Array.isArray(db.projects))
    ? db.projects.find(x => x && x.id === activeProjectId) : null;
  if (renameProject(activeProjectId, el.textContent || '')) return;
  el.textContent = (cur && cur.name) || '[Project Name]';
}

let SESSIONS = [];

/* ── v3.0: STAKEHOLDERS + RISKS (new top-level data containers) ─────────
   STAKEHOLDERS: array of stakeholder objects (see schema below).
   RISKS: empty placeholder array — Risks tab is an empty scaffold in v3.0,
     populated in v3.1+. Declared here so port script can transplant it
     forward when v3.1 ships.
   appMeta.dashboard: object with at minimum a 'notes' string. Dashboard
     tab v3.0 has a single "Project notes" textarea bound to this field.

   Stakeholder object schema:
     {
       id: number,                       // unique ID
       name: string,                     // single-string name field
       role: string,
       organisation: string,                  // v3.0: organization/employer
       power: 'low'|'medium'|'high',
       interest: 'low'|'medium'|'high',
       engagementCurrent: 'Unaware'|'Resistant'|'Neutral'|'Supportive'|'Leading',
       engagementTarget: 'Unaware'|'Resistant'|'Neutral'|'Supportive'|'Leading',
       notes: string
     }
*/
let STAKEHOLDERS = [];
let RISKS = [];
/* v8.4: RACI/RAM cell values, and the per-project Assumptions log. */
const RACI_CODES = ['R', 'A', 'C', 'I'];
const ASSUMPTION_CONFIDENCE = ['High', 'Medium', 'Low'];
const ASSUMPTION_VALIDATION = ['Unvalidated', 'Validated', 'Invalidated'];
let ASSUMPTIONS = [];
function _normAssumption(a) {
  if (typeof a.statement !== 'string') a.statement = (typeof a.assumption === 'string' ? a.assumption : (typeof a.text === 'string' ? a.text : ''));
  if (typeof a.owner !== 'string') a.owner = '';
  if (!ASSUMPTION_CONFIDENCE.includes(a.confidence)) a.confidence = 'Medium';
  if (!ASSUMPTION_VALIDATION.includes(a.validation)) a.validation = 'Unvalidated';
}
function _nextRiskSeq() {
  /* v8.4: register-wide self-healing high-water mark (mirrors appMeta.crSeq) — a
     risk/issue number is never reused after the highest is deleted; gaps are allowed. */
  /* v14 (W3b): writer-scoped block minting. The stored seq is still honoured, but only
     inside MY block - see _mintNextId. */
  const n = _mintNextId(RISKS, _myWriterId(), appMeta.riskSeq);
  appMeta.riskSeq = n;
  return n;
}
function _nextAssumptionSeq() {
  const n = _mintNextId(ASSUMPTIONS, _myWriterId(), appMeta.asmSeq);   /* v14 (W3b) */
  appMeta.asmSeq = n;
  return n;
}
if (!appMeta.dashboard || typeof appMeta.dashboard !== 'object') appMeta.dashboard = {};
if (typeof appMeta.dashboard.notes !== 'string') appMeta.dashboard.notes = '';
let nextStakeholderId = _nextNumericId(STAKEHOLDERS);   /* v5.2.3: numeric ids only — never NaN */
/* v10.3 (W4): mint a stakeholder id and persist the high-water mark, so a number is never
   reused after the highest holder is deleted (RACI + comms reference these ids by value).
   LAZY like crSeq/comSeq — appMeta.stkSeq only appears once a stakeholder has been added,
   so a file that never adds one still round-trips byte-identically. */
function _nextStkSeq() {
  const id = nextStakeholderId++;
  appMeta.stkSeq = id;
  return id;
}

/* v3.4: stakeholder CATEGORY — a closed set, default 'Customer'. Older sources
   (pre-v3.4) carry no s.category, so this normalization assigns the default; it
   also guards against any out-of-set value sneaking in. Category is a separate
   colour family from the risk severity scale, so it never reads as "status". */
const STK_CATEGORIES = ['Customer', 'Internal', 'Partner', 'Other'];
/* ══ v14.1 (d): THE CATEGORY A NEW STAKEHOLDER STARTS ON ═══════════════════════════════
   His call: 'when adding a stakeholder, default category should be Internal.' Customer was
   the wrong guess most of the time.
   ⚠ IT IS A CONSTANT BECAUSE TWO PLACES MUST AGREE, AND THEY ALREADY FAILED TO ONCE.
   `_stkIsBlank` decides whether an abandoned drawer is thrown away, and it does so by
   comparing the category against THIS default - 'a pick that equals the default is
   indistinguishable from never having touched the control'. Change the seed alone and every
   abandoned drawer starts committing an empty stakeholder; that is v13.1's A62, which cost
   a release. One name, read by both, so the pair cannot drift again.
   ⚠ This is NOT the fallback for existing records. `STK_CATEGORIES.includes(s.category) ?
   s.category : 'Customer'` at the read sites means 'this record has no usable category,
   show it as Customer' and is about DATA ALREADY ON FILE. Moving those would silently
   recategorise real people. Only creation and the blank-test read this. */
const STK_NEW_DEFAULT_CATEGORY = 'Internal';
STAKEHOLDERS.forEach(s => {
  if (typeof s.category !== 'string' || !STK_CATEGORIES.includes(s.category)) s.category = 'Customer';
});

/* v3.3: extended RISKS schema — each risk gets type/category/strategy/status/owner/notes
   in addition to the existing id/statement/probability/impact fields. Older sources
   (v3.0/v3.1/v3.2) ship RISKS = [], so the normalization below is a no-op for them;
   it only kicks in for v3.3+ files with populated risks. */
/* v8.4: risk/issue ids now come from the self-healing _nextRiskSeq() (appMeta.riskSeq),
   so a number is never reused after the highest is deleted. */
const RISK_VALID_TYPES = ['threat', 'opportunity'];
const RISK_VALID_LEVELS = ['low', 'medium', 'high'];
const RISK_VALID_STATUSES = ['Open', 'Occurred', 'Closed'];
const RISK_VALID_CATEGORIES = ['Technical', 'External', 'Organizational', 'Project Management'];
/* ── v13.1 (A39, ◆1): "Organizational" is the one American spelling the user can see, and
   it is PMBOK-verbatim. His decision: KEEP THE STORED TOKEN and display the British form
   through this map at the render sites. Renaming the value would be a data migration — it
   sits in 6 live risk records — for a cosmetic gain, which is the wrong trade.
   ⚠ So: `RISK_VALID_CATEGORIES` remains the storage vocabulary and is what validation,
   comparison and the data file all use. This map is DISPLAY ONLY. A token with no entry
   falls through to itself, so adding a category needs no change here unless its British
   spelling differs. */
const RISK_CATEGORY_LABEL = { 'Organizational': 'Organisational' };
function riskCategoryLabel(c) { return RISK_CATEGORY_LABEL[c] || c; }
const RISK_THREAT_STRATEGIES = ['Escalate', 'Avoid', 'Transfer', 'Mitigate', 'Accept'];
const RISK_OPPORTUNITY_STRATEGIES = ['Escalate', 'Exploit', 'Share', 'Enhance', 'Accept'];
/* v8.4: unified Risk & Issue register (promote-in-place). A record carries an explicit
   `phase` ('risk' | 'issue') — this retires the v8.2 sticky `hasOccurred` pill hack. A
   THREAT set to status Occurred PROMOTES in place to an issue (same record): the
   probability × impact assessment demotes to a read-only History line and the issue
   working-fields appear. Flipping it back to Open demotes to the risk phase but RETAINS the
   issue fields (hidden), so a mistaken promote loses nothing. Opportunities never promote
   (no issue phase; the old derived "Benefit" label is dropped — real benefits live on the
   dashboard). Colours are unchanged. The register id is the record's number for life, shown
   as R-<id> in the risk phase and ISS-<id> once promoted (the number is identity). */
const ISSUE_SEVERITIES = ['High', 'Medium', 'Low'];
const ISSUE_WORKSTATUSES = ['Open', 'In progress', 'Resolved'];
/* ── v10.3 (W4): ONE local-date formatter for the whole app ──────────────────────────
   Every "what day is it" bug in this file came from `toISOString().slice(0,10)`, which
   converts to UTC first: at a positive offset (CET/CEST) that names YESTERDAY between
   local midnight and the offset — and the Focus view, which applied it to a local-midnight
   Date, was a day behind ALL day. Date-only values here are calendar dates in the user's
   own timezone, so they must be formatted from the local getters, never via UTC.
   `_isoLocal(d)` formats any Date; `_todayISO()` is today. Do not reintroduce a UTC variant. */
function _isoLocal(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
function _todayISO() { return _isoLocal(new Date()); }
/* v14 (W3b): the number shown is DERIVED from position, never the stored id - two writers
   offline both mint "the next risk" and only one of them can be shown as R-004. Proven
   against both real data files to renumber nothing today (qa/merge_props.js). */
function _registerId(r) { return (r.phase === 'issue' ? 'ISS-' : 'R-') + String(_mintDisplayOrd(RISKS, r)).padStart(3, '0'); }
function _riskPillLabel(r) {
  if (r.phase === 'issue') return 'Issue';
  return (r.type === 'threat') ? 'Threat' : 'Opportunity';
}
function _severityDefault(r) {
  /* sensible seed for a freshly promoted issue: derive from the risk's impact */
  if (r.impact === 'high') return 'High';
  if (r.impact === 'low') return 'Low';
  return 'Medium';
}
function _ensureIssueFields(r) {
  /* ⚠⚠ v16.7 W4 (P3-F2): THIS USED TO STAMP TODAY'S DATE WHEN `raised` WAS ABSENT, AND A DATE
     COMES FROM THE MACHINE'S CLOCK RATHER THAN FROM THE FILE. §v16.6 P3 reproduced the result: the
     same record stamped 2026-08-26 on one colleague's machine and 2026-09-02 on another's, purely
     because they opened it on different days; whoever saved first wrote their date in. So an
     issue's "raised" date really meant "the day the first person to open it after it occurred
     happened to open it" — which UNDERSTATES ISSUE AGEING, and ageing is exactly what that field
     is read for. ⚠ It is P1-F1's shape and WORSE IN KIND: `displayNo` at least derives from the
     file, so machines can in principle agree; a clock cannot.
     ⚠ THE PROMOTION PATH IS UNAFFECTED, which is why this is safe: _setRiskStatus stamps `raised`
     AT THE MOMENT a threat is marked Occurred (see below), so a normal promotion still records the
     real day. What is removed is INVENTING one for a record that arrived without it — legacy data
     or an import. Left absent, the register shows it as unknown, which is v10.4.2's rule applied
     to a field rather than a message: state what is known, never what is guessed. */
  /* ⚠ LAZY, not eager: a record that arrived WITHOUT `raised` keeps no key at all. Writing an
     empty string would still be adding a stored key to a record that never had one, and the
     register already renders a missing value safely (`r.raised || ''`). Only a present value of
     the wrong type is corrected. */
  if (r.raised !== undefined && typeof r.raised !== 'string') r.raised = '';
  if (!ISSUE_SEVERITIES.includes(r.severity)) r.severity = _severityDefault(r);
  if (!ISSUE_WORKSTATUSES.includes(r.workStatus)) r.workStatus = 'Open';
  if (typeof r.resolution !== 'string') r.resolution = '';
}
function _setRiskStatus(r, newStatus) {
  r.status = newStatus;
  /* Promote-in-place is threat-only and status-driven. */
  if (r.type === 'threat') {
    if (newStatus === 'Occurred') {
      if (r.phase !== 'issue') {
        r.phase = 'issue';
        /* ⚠ v16.7 W4 (P3-F2): the date is stamped HERE, where the promotion actually happens and
           TODAY genuinely is the day it occurred — not in the normaliser, which runs on every
           project activation on every machine and cannot know when anything happened. */
        if (typeof r.raised !== 'string' || !r.raised) r.raised = _todayISO();
        _ensureIssueFields(r);
      }
    } else if (newStatus === 'Open') {
      r.phase = 'risk';            /* demote; issue fields are retained (hidden) */
      r.direct = false;            /* a demoted record is a genuine risk now */
    }
    /* 'Closed': leave phase as-is (a threat closed after occurring stays an issue) */
  }
}
/* Normalise one risk/issue record in place (unknown keys survive). Hoisted so the
   module-load pass below and the per-project load pass can share it. */
function _normRisk(r) {
  if (!RISK_VALID_TYPES.includes(r.type)) r.type = 'threat';
  if (typeof r.statement !== 'string') r.statement = (typeof r.title === 'string' ? r.title : '');
  if (!RISK_VALID_CATEGORIES.includes(r.category)) r.category = 'Technical';
  if (!RISK_VALID_LEVELS.includes(r.probability)) r.probability = 'medium';
  if (!RISK_VALID_LEVELS.includes(r.impact)) r.impact = 'medium';
  if (!RISK_VALID_STATUSES.includes(r.status)) r.status = 'Open';
  if (r.phase !== 'risk' && r.phase !== 'issue') r.phase = (r.type === 'threat' && r.status === 'Occurred') ? 'issue' : 'risk';
  if (typeof r.direct !== 'boolean') r.direct = false;
  const validStrategies = (r.type === 'threat') ? RISK_THREAT_STRATEGIES : RISK_OPPORTUNITY_STRATEGIES;
  if (!validStrategies.includes(r.strategy)) r.strategy = validStrategies[validStrategies.length - 1]; /* default Accept */
  if (typeof r.owner !== 'string') r.owner = '';
  if (typeof r.notes !== 'string') r.notes = '';
  if (r.phase === 'issue') _ensureIssueFields(r);
}
RISKS.forEach(_normRisk);

/* ── Probability × impact — ONE definition for the whole app ─────────────────────────
   v13.1 (A37): this arithmetic existed THREE times — beside the Key risks list, again
   beside the register cards, and a third time inlined in the Focus view behind a
   `typeof _riskScoreInfo === 'function'` guard on our OWN hoisted function, which this
   codebase bans: guarding your own code can only ever hide its absence (the v11.1 lesson,
   where a guarded call to a function that never existed painted nothing for two versions).
   Two of the three also spoke different band words — 'medium' in one, 'med' in the other —
   so nothing connected the dashboard's "Medium" to the register's `sev-med`, and a change
   to the scale would have had to be found in three places by memory.
   ⚠ The CSS class families are NOT a second vocabulary: `sev-*` (register) and `lvl-*`
   (dashboard) are class names, mapped from the one band token at the render site.
   Defined HERE, in the model block above every renderer, so no caller can reach a const
   in its temporal dead zone — the reason this is not simply left where it was found. */
const _RISK_RANK = { low: 1, medium: 2, high: 3 };
const _RISK_CAP = s => (typeof s === 'string' && s.length) ? s.charAt(0).toUpperCase() + s.slice(1) : '';
/* Score = probability × impact on the 3-level scale (L/M/H = 1/2/3). An unset or unknown
   level ranks 0, so the score is 0 and the band is Low — the same answer all three
   implementations gave, kept deliberately rather than quietly changed. */
function _riskScore(r) {
  const p = _RISK_RANK[r.probability] || 0;
  const i = _RISK_RANK[r.impact] || 0;
  return p * i;
}
/* Bands: 1-2 Low, 3-4 Medium, 6-9 High (5, 7 and 8 are unreachable on a 3×3). */
function _riskBand(score) { return score >= 6 ? 'high' : score >= 3 ? 'medium' : 'low'; }
function _riskScoreInfo(r) { const score = _riskScore(r); return { score, level: _riskBand(score) }; }
/* band token → the two CSS families, and the opportunity wording. For an opportunity the
   top-right corner is the BEST case, which is why it gets its own words (A18/A44). */
const _SEV_CLASS = { low: 'sev-low', medium: 'sev-med', high: 'sev-high' };
const _OPP_CLASS = { low: 'opp-low', medium: 'opp-med', high: 'opp-high' };
const _OPP_WORD  = { low: 'Low potential', medium: 'Moderate', high: 'High potential' };

/* ── v3.2: Success Projects (new top-level container) ───────────────────────
   Each project: { id:number, title:string, scope:string, actionIds:[number] }
   An action id appears in at MOST one project's actionIds (one-project-per-action).
   This array is serialized by exportFile the same way as STAKEHOLDERS/RISKS. */
let WORKSTREAMS = [];
/* Shared lifecycle vocabulary for programmes and P5 WBS elements. Its position is early
   because the first WBS render precedes the portfolio controller block. */
const PROG_LIFECYCLE = [
  ['not-started', 'Not started'], ['in-progress', 'In progress'], ['on-hold', 'On hold'],
  ['completed', 'Completed'], ['cancelled', 'Cancelled']
];
/* v17.5 (WS-03): card expansion is personal, transient session state. Key it by the
   stable project UID because workstream ids are local to a project and routinely collide.
   Nothing here is captured in appMeta.uiState, localStorage or the saved data file. */
const _wsExpandedByProject = new Map();
let _wsRenderedProjectUid = null;
/* v8.2.3 (Q7): workstream id counter (recomputed per project in setActiveProject). Named
   `nextProjectId` before v8.2.3 — a misnomer, since top-level projects use slug ids from
   _uniqueProjectId(name), not this counter. Initialised from the workstreams (WORKSTREAMS). */
let nextWorkstreamId = WORKSTREAMS.length ? Math.max(...WORKSTREAMS.map(p => p.id)) + 1 : 1;
/* ── ⚠⚠ v16.8 (W5): A WORKSTREAM NUMBER IS NEVER REUSED ───────────────────────────
   `_nextStkSeq`'s idiom, applied to the third of the three registers that never had it.
   ⚠⚠ SCHEMA-CONSENT 2026-08-27 — `appMeta.wsSeq`, given separately from the wave's Go, which
   is what the second absolute rule requires. LAZY like every other `*Seq`: the key appears
   only once a workstream has been added, so a project that never adds one still round-trips
   byte-identically and `normalizeAppMeta` must never backfill it.
   ⚠ MEASURED BEFORE IT WAS BUILT (`qa/w5_compat_probe.py`): the v16.3 PRODUCTION shell, v16.7
   and v16.8 all open a file carrying this key, read it, save it back, re-parse their own
   output and merge two parts without losing it — appMeta serialises wholesale, so nothing
   rebuilds it field by field. */
function _nextWsSeq() {
  const id = nextWorkstreamId++;
  appMeta.wsSeq = id;
  return id;
}
/* Normalize each project's shape defensively (older/partial data). */
WORKSTREAMS.forEach(p => {
  if (typeof p.title !== 'string') p.title = '';
  if (typeof p.scope !== 'string') p.scope = '';
  if (!Array.isArray(p.actionIds)) p.actionIds = [];
  p.actionIds = p.actionIds.filter(x => typeof x === 'number');
});

/* appMeta sub-fields (rag, stakeholderSort, pickerOpenOnly, gridDotColorByCategory,
   meetingTimer — v10.2 removed the dormant v3-era cadence/products/crmUrl keys) are
   defaulted via the shared normalizeAppMeta() so boot and per-project switch use one
   source of truth. */
normalizeAppMeta();
let stakeholderSearchQuery = '';

/* ── ATTENDEES MIGRATION (v1.20) ───────────────────────────────────────────
   Data model is now `attendeeRows: [{name, company}]` — one row per person.
   This block migrates ANY older format into the new shape and is idempotent:
   - v1.19 `attendeeGroups: [{company, names}]` (where `names` was \n- or comma-separated)
   - very-old `attendees: "Name, Name, Name"` string
   - missing entirely → empty array
   It also splits names that previously got smushed together due to single-line
   <input> rendering of \n-separated strings, so loading an old file fixes itself. */
function _splitNames(str) {
  // Split on newlines, semicolons, or commas; trim and discard empties
  return String(str || '').split(/[\n;,]+/).map(n => n.trim()).filter(Boolean);
}
SESSIONS.forEach(s => {
  if (!Array.isArray(s.attendeeRows)) {
    s.attendeeRows = [];
    if (Array.isArray(s.attendeeGroups)) {
      s.attendeeGroups.forEach(g => {
        const co = (g.organisation || '').trim();
        _splitNames(g.names).forEach(n => s.attendeeRows.push({ name: n, organisation: co }));
      });
    } else if (typeof s.attendees === 'string' && s.attendees.trim()) {
      _splitNames(s.attendees).forEach(n => s.attendeeRows.push({ name: n, organisation: '' }));
    }
    delete s.attendeeGroups;
  }
  if (s.startTime === undefined) s.startTime = s.time || '';
  if (s.endTime === undefined) s.endTime = '';
  if (s.time !== undefined) delete s.time;
  if (s.title === undefined) s.title = '';
  // Recompute preview string from rows so legacy s.attendees string is always in sync
  s.attendees = formatAttendeesPreview(s.attendeeRows);
});

/* Build a compact display string from rows, grouped by organisation:
   "AcmeCorp: Alice Smith, Bob Jones · TrendAI: Casey Doe (host)" */
function formatAttendeesPreview(rows) {
  if (!Array.isArray(rows) || !rows.length) return '';
  const groups = {};
  const order = [];
  rows.forEach(r => {
    if (!r || !r.name || !r.name.trim()) return;
    const co = (r.organisation || '').trim();
    if (!(co in groups)) { groups[co] = []; order.push(co); }
    groups[co].push(r.name.trim());
  });
  return order.map(co => co ? co + ': ' + groups[co].join(', ') : groups[co].join(', ')).join(' · ');
}


/* ── v13.1 (A45, ◆3): the meeting-time zone is the VIEWER's, not Germany's ───────────
   getCETZone() carried its own table of German DST switch dates and stamped CET/CEST onto
   every meeting for every reader. That is wrong for anyone outside central Europe — and
   §v14 makes a second reader the normal case, which is why ◆3 chose to derive it rather
   than drop the suffix. The browser already knows: ask it for the short zone name AT THE
   MEETING'S DATE, so summer/winter is handled without a rules table that can go stale.
   Reads "BST" in London, "GMT+2" in Berlin, "EDT" in New York — each correct for whoever
   is looking. Locale pinned to en-GB, the file's convention (see formatDateDisplay).
   ⚠ The stored data is unchanged: a meeting stores a date and a wall-clock time, exactly
   as before. Only the LABEL beside it is derived, so nothing in the file moves. */
/* ══ v14.1 (h): THE LABEL BELONGS TO THE AUTHOR'S CLOCK, NOT THE READER'S ═══════════════
   `_viewerZone(date)` asked THE READER'S browser for its zone and appended it to a
   wall-clock time the AUTHOR typed. So 07:00 entered in Munich was printed to a colleague
   in Singapore as "07:00 GMT+8" — a meeting that is really 13:00 there. No stored data was
   wrong; the sentence on screen was, and that is worse than no label at all: it turns an
   honest ambiguity into a confident false statement.
   ⚠ THE REASONING THAT PUT IT THERE WAS SOUND, AND ITS PREMISE MOVED. v2.18's getCETZone()
   carried a table of German DST dates and stamped CET/CEST on every meeting for every
   reader; v13.1 (A45, ◆3) replaced it with this, citing §v14 making a second reader normal,
   and concluded it read "each correct for whoever is looking". That last clause is the
   flaw, and it is only visible from v14: the label describes the reader, the time belongs
   to the author.
   ⚠ ◆3's REAL WIN IS KEPT: the browser still owns the DST rules — one Intl call with the
   STORED zone at the MEETING'S date — so there is no rules table of ours to go stale. That
   is also why the picker's list comes from the browser rather than from a constant here.
   ⚠ NO ZONE MEANS NO LABEL. A meeting written before this version stores nothing, and
   silence is the honest answer; falling back to the reader's zone would be today's bug
   under a new name. Parameterising the function is what makes that unrepresentable. */
/* ══ v14.2 (c): THE TIMEZONE IS WHATEVER THE USER CALLS IT ═════════════════════════════
   ⚠ THIS REPLACES v14.1's DESIGN ONE VERSION LATER, on his instruction, and it is the better
   one. v14.1 stored an IANA zone and COMPUTED the label ("Europe/Berlin" -> "CEST" at the
   meeting's date). Three complaints, all real: the datalist offered nothing because the field
   arrived seeded with a full valid value; "New York" never matched America/New_York because a
   space is not an underscore; and "CEST" was refused outright, correctly and uselessly.

   HIS DESIGN: store the string, never interpret it. The suggestion list is DERIVED from the
   labels already in use, exactly as updateOrganisationDatalist derives organisations from
   STAKEHOLDERS - so it self-populates, needs no storage of its own, and merges across writers
   for free because it is derived from records that already merge.

   ⚠ WHAT IT GIVES UP, AND WHY THAT IS NOT A LOSS. The label no longer self-corrects across
   DST: "CEST" written in August still reads CEST in January. HIS ANSWER, which overturned my
   objection: that IS the record. "if someone wrote CEST instead of Berlin in July, then
   looking at that old meeting makes me know it was summer time." A computed label would have
   shown CET in January and quietly erased that fact. On a MEETING RECORD, which is what
   minutes are, the typed label carries strictly MORE information than the clever version.

   ⚠ DELETED WITH THIS: _zoneLabel, _myZone, _isUsableZone and the 418-zone
   _populateTimezoneList. There is no zone table, no abbreviation derivation and no validation
   left in the app, because there is nothing left to validate. */

/* The suggestion list: every label already used on a meeting, deduped and sorted. The
   organisation datalist's shape exactly - see updateOrganisationDatalist. */
function updateTimezoneDatalist() {
  const used = (Array.isArray(SESSIONS) ? SESSIONS : [])
    .reduce((acc, s) => {
      if (s && typeof s.tz === 'string' && s.tz.trim()) acc.push(s.tz.trim());
      if (s && typeof s.nextTz === 'string' && s.nextTz.trim()) acc.push(s.nextTz.trim());
      return acc;
    }, []);
  const dl = document.getElementById('timezoneList');
  if (dl) dl.innerHTML = [...new Set(used)].sort().map(z => `<option value="${esc(z)}"></option>`).join('');
}

/* Set or clear a meeting's zone. Blank DELETES the key (the allocation-%/location rule), so a
   file whose meetings never carry one round-trips byte-identically.
   ⚠ NOTHING IS REFUSED. That was the entire complaint, and free text is the entire fix. */
function saveSessionZone(id, el) {
  const s = SESSIONS.find(x => x.id === id);
  if (!s || !el) return;
  const v = String(el.value || '').trim();
  if (!v) delete s.tz; else s.tz = v;
  markDirty();
  updateTimezoneDatalist();   /* a new label teaches the list immediately */
  updateSessionHeader(id);
}

/* ⚠ v14.2 (c): the NEXT-MEETING block gets its OWN zone. ◆h1 had them share one, recorded as
   a deliberate simplification with its own exit clause - "If it ever bites, that is a new
   field and should be recognised as one." He hit it on his first real day, so per its own
   terms this is that new field. LAZY: stored only when it DIFFERS from the meeting's own, so
   a file that never uses it round-trips byte-identically. */
function saveSessionNextZone(id, el) {
  const s = SESSIONS.find(x => x.id === id);
  if (!s || !el) return;
  const v = String(el.value || '').trim();
  if (!v || v === (s.tz || '')) delete s.nextTz; else s.nextTz = v;
  markDirty();
  updateTimezoneDatalist();
  updateSessionHeader(id);
}

function formatTimeRange(s) {
  if (!s.startTime && !s.endTime) return '';
  /* v14.2 (c): print what is STORED. It is the author's own words about which clock they
     meant, so it is still the author's zone and never the reader's - v14.1 (h)'s invariant,
     now held by construction rather than by a parameterised lookup. */
  const zone = (typeof s.tz === 'string') ? s.tz.trim() : '';
  /* v14.1 (h): a meeting with no stored zone prints no suffix AND no stray trailing space.
     The old form always appended ' ' + zone because a zone was always derivable. */
  const _z = zone ? ' ' + zone : '';
  if (s.startTime && s.endTime) return s.startTime + '–' + s.endTime + _z;
  if (s.startTime) return s.startTime + _z;
  if (s.endTime) return '–' + s.endTime + _z;
  return '';
}

/* v2.18: convert 24h "HH:MM" to 12h "H:MM AM/PM". Returns '' for empty input. */
function to12h(t) {
  if (!t) return '';
  const parts = String(t).split(':');
  const hh = parseInt(parts[0], 10);
  const mm = parts[1] || '00';
  if (isNaN(hh)) return '';
  const period = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh === 0 ? 12 : (hh > 12 ? hh - 12 : hh);
  return h12 + ':' + String(mm).padStart(2, '0') + ' ' + period;
}

/* ⚠ v13.1 (A61): markDirty() goes AFTER the guard, never before it. These handlers used to
   dirty the file and only then discover there was nothing to change — a no-op left the tab
   claiming unsaved work and armed the leave-site prompt over an edit that never happened.
   moveAttendeeRow() below already had the right order; the other three did not. */
function saveAttendeeField(sessionId, rowIndex, field, value) {
  const s = SESSIONS.find(x => x.id === sessionId);
  if (!s || !s.attendeeRows[rowIndex]) return;
  markDirty();
  s.attendeeRows[rowIndex][field] = value;
  s.attendees = formatAttendeesPreview(s.attendeeRows);
  updateSessionHeader(sessionId);
  /* ⚠ v16 (b), option C: the optional pill is live-enabled the moment the row HAS a name, and
     it is updated IN PLACE rather than by re-rendering. This function has always avoided a
     re-render deliberately — it runs on every keystroke, and repainting the row would destroy
     the caret. Toggling `disabled` changes no layout, so the row does not move either. */
  if (field === 'name') {
    const card = document.getElementById('session-' + sessionId);
    const grp = card ? card.querySelectorAll('.attendee-group')[rowIndex] : null;
    const pill = grp ? grp.querySelector('.att-opt') : null;
    if (pill) pill.disabled = !String(value || '').trim();
  }
}

/* v5.1 §3: per-attendee present flag. Absent → write present:false; present (default)
   → delete the field, so untouched rows round-trip unchanged. No re-render needed. */
/* v16 (b): per-attendee OPTIONAL flag. Required is the default, so `optional` is written ONLY
   when it is on and the key is DELETED when it is off — the lazy idiom `present` has used since
   v5.1 §3, mirrored. Meetings already on disk therefore round-trip byte-identically until
   somebody actually sets the flag.
   ⚠ ORTHOGONAL TO `present`, and the two must never be conflated: optional describes the
   INVITATION (did they have to be here), present describes ATTENDANCE (did they come).
   ⚠ The app never computes anything from this — it is a label, on the v14.1 stakeholder-location
   precedent. No quorum logic, and none is to be added without a decision.
   v17.0 W2: copyMeetingPost reads the same lazy flag for its Optional column; no second field
   or computed attendance rule is introduced. */
function toggleAttendeeOptional(sessionId, rowIndex) {
  const s = SESSIONS.find(x => x.id === sessionId);
  if (!s || !Array.isArray(s.attendeeRows) || !s.attendeeRows[rowIndex]) return;
  markDirty();
  const row = s.attendeeRows[rowIndex];
  if (row.optional === true) delete row.optional; else row.optional = true;
  renderSessions();
}

function toggleAttendeePresent(sessionId, rowIndex, present) {
  const s = SESSIONS.find(x => x.id === sessionId);
  if (!s || !Array.isArray(s.attendeeRows) || !s.attendeeRows[rowIndex]) return;
  if (present) delete s.attendeeRows[rowIndex].present; else s.attendeeRows[rowIndex].present = false;
  markDirty();
}

function addAttendeeRow(sessionId) {
  syncSessionsFromDOM();  // capture any pending input before renderSessions wipes the DOM
  const s = SESSIONS.find(x => x.id === sessionId);
  if (!s) return;
  markDirty();                            /* v13.1 (A61): after the guard, not before */
  // Smart prefill: copy company from the previous row so users don't retype it for each colleague
  const prev = s.attendeeRows[s.attendeeRows.length - 1];
  const prefillOrganisation = (prev && prev.organisation) ? prev.organisation : '';
  s.attendeeRows.push({ name: '', organisation: prefillOrganisation });
  renderSessions();
  const card = document.getElementById('session-' + sessionId);
  if (card) {
    card.classList.add('expanded');
    // Focus the newly-added Name input (last one rendered)
    const inputs = card.querySelectorAll('.attendee-name');
    const last = inputs[inputs.length - 1];
    if (last) last.focus();
  }
}

function removeAttendeeRow(sessionId, rowIndex) {
  syncSessionsFromDOM();  // capture any pending input before renderSessions wipes the DOM
  const s = SESSIONS.find(x => x.id === sessionId);
  if (!s || !s.attendeeRows[rowIndex]) return;
  markDirty();                            /* v13.1 (A61): after the guard, not before */
  s.attendeeRows.splice(rowIndex, 1);
  s.attendees = formatAttendeesPreview(s.attendeeRows);
  renderSessions();
  const card = document.getElementById('session-' + sessionId);
  if (card) card.classList.add('expanded');
}

/* v2.16: reorder attendees up/down within a session */
function moveAttendeeRow(sessionId, rowIndex, direction) {
  const s = SESSIONS.find(x => x.id === sessionId);
  if (!s || !Array.isArray(s.attendeeRows)) return;
  const target = rowIndex + direction;
  if (target < 0 || target >= s.attendeeRows.length) return;  // boundary guard
  markDirty();
  syncSessionsFromDOM();  // capture any pending input before renderSessions wipes the DOM
  // Re-resolve s.attendeeRows after sync (the array reference is preserved but values may have updated)
  const rows = s.attendeeRows;
  if (target < 0 || target >= rows.length) return;
  const tmp = rows[rowIndex];
  rows[rowIndex] = rows[target];
  rows[target] = tmp;
  s.attendees = formatAttendeesPreview(rows);
  renderSessions();
  const card = document.getElementById('session-' + sessionId);
  if (card) card.classList.add('expanded');
  updateSessionHeader(sessionId);
}


/* ── ACTION EDITS ── */
/* Self-healing init: creates entries for fresh actions, backfills new fields on older saved files */
// Lookup tables: kebab-case keys → display-format values used by the UI <select> options
const STATUS_DISPLAY = { 'in-progress':'In progress', 'not-started':'Not yet started', 'on-hold':'On hold', 'completed':'Completed', 'cancelled':'Cancelled' };
const PRIORITY_DISPLAY = { 'high':'High', 'medium':'Medium', 'low':'Low' };

/* == v17.4 (W2, F-B-01): ONE ID-FREE LEGACY NOTE HAS ONE COMPARISON IDENTITY =============
   `initActionEdits` used to call makeNoteId() while seeding an old note that predates noteId.
   `_refreshActionOverlays` seeds the local overlay, previous record and incoming record
   independently; three random IDs therefore made unchanged history look like unsaved typing.
   The stale local overlay then beat a peer's newer history on the next Save.

   Legacy identity is derived only from the action and the note's ordered position. Position is
   required because two old notes may have identical text and time. Existing IDs are never
   changed. The deterministic value lives first in the detached overlay and reaches the record
   only through a successful Save, exactly like the previous self-heal. Genuinely new notes still
   use makeNoteId(); Phase 5 makes both allocation paths collision-aware. No stored key or type changes. */
function _legacyNoteId(actionId, index) {
  return 'n_legacy_' + encodeURIComponent(String(actionId)) + '_' + String(index + 1);
}
/* == v17.4 (W4, F-D-03): NOTE IDENTITIES ARE UNIQUE WITHIN THEIR ACTION ====================
   Date.now + Math.random is still the ordinary base so existing output stays familiar. The
   action's complete note history is the authority for collisions: keep the base when free,
   otherwise use a deterministic suffix. The finite bound turns a broken dependency or malformed
   imported history into a clear failure instead of an unbounded loop. Claims are added only on
   success, so an exhausted allocation leaves its caller's set unchanged. */
const _NOTE_ID_SUFFIX_LIMIT = 128;
function _claimUniqueNoteId(base, taken) {
  if (!taken.has(base)) { taken.add(base); return base; }
  for (let n = 2; n <= _NOTE_ID_SUFFIX_LIMIT; n++) {
    const candidate = base + '_' + n;
    if (!taken.has(candidate)) { taken.add(candidate); return candidate; }
  }
  throw new Error('Could not allocate a unique note identifier within the bounded suffix limit.');
}
function initActionEdits(a, ed) {
  /* ── v16.8 (W1): the edits map is a PARAMETER now, defaulting to the module binding ───
     The save bake needs it because it now bakes EVERY project, and a non-active project's
     overlay lives on that project rather than in the binding setActiveProject re-points.
     ⚠⚠ THE TYPE TEST IS LOAD-BEARING, NOT DEFENSIVE PADDING. Three callers pass this
     function straight to forEach — `ACTIONS.forEach(initActionEdits)` at two sites and
     `newActions.forEach(initActionEdits)` at a third — so the second argument they supply is
     the array INDEX. A bare `ed || edits` therefore worked for index 0 and silently wrote
     into a NUMBER for every action after the first, and the whole battery collapsed on the
     first read of `e.status`. ⚠ I found the three sites only after that failure: a grep for
     `initActionEdits(` cannot see a bare function reference, which is precisely the
     count-the-sites rule this repo keeps re-learning. */
  ed = (ed && typeof ed === 'object') ? ed : edits;
  const e = ed[a.id] = ed[a.id] || {};
  // Backward compat: migrate old `notes` field to `details` (one-way) if present
  if (a.notes !== undefined && a.details === undefined) a.details = a.notes;
  if (e.notes !== undefined && e.details === undefined) { e.details = e.notes; delete e.notes; }
  // Normalize kebab-case status/priority to display format on first read.
  // If already display-format (saved file), the lookup misses and the value is kept as-is.
  if (e.status === undefined) e.status = STATUS_DISPLAY[a.status] || a.status;
  if (e.priority === undefined) e.priority = PRIORITY_DISPLAY[a.priority] || a.priority;
  if (e.due === undefined) e.due = a.due || '';
  if (e.owner === undefined) e.owner = a.owner;
  if (e.desc === undefined) e.desc = a.desc;
  if (e.source === undefined) e.source = a.source;
  if (e.date === undefined) e.date = a.date;
  if (e.details === undefined) e.details = a.details || '';
  /* v2.40: labels replaces category. Default to empty array. Old `a.category` not migrated. */
  if (e.labels === undefined) e.labels = Array.isArray(a.labels) ? [...a.labels] : [];
  if (e.requestor === undefined) e.requestor = a.requestor || '';
  /* v2.20: closedDate field — null | YYYY-MM-DD. Empty means 'not closed' or
     'closed but never had a closedDate auto-filled' (legacy closed actions). */
  if (e.closedDate === undefined) e.closedDate = a.closedDate || '';
  /* v2.21: dependsOn (blocking) and relatedTo (soft) link arrays. */
  if (e.dependsOn === undefined) e.dependsOn = Array.isArray(a.dependsOn) ? [...a.dependsOn] : [];
  if (e.relatedTo === undefined) e.relatedTo = Array.isArray(a.relatedTo) ? [...a.relatedTo] : [];
  /* v2.22: progress (0-100 | null) and flagged (boolean) */
  if (e.progress === undefined) e.progress = (typeof a.progress === 'number') ? a.progress : null;
  if (e.flagged === undefined) e.flagged = !!a.flagged;
  /* v2.90: parentId for sub-action support. null = top-level (default).
     A sub-action has parentId = id of its parent (top-level only — single-level nesting). */
  if (e.parentId === undefined) e.parentId = (typeof a.parentId === 'number') ? a.parentId : null;
  /* == v17.3 (W4.2): SEEDED FROM THE RECORD LIKE EVERY OTHER FIELD, not just defaulted =====
     This was `if (!e.updates) e.updates = [];` - the one field here that did NOT come from the
     action. It survived because notes normally reach a session through the PERSISTED overlay, so
     the record's copy was written by the bake and never read back.
     THE DAY IT STOPPED BEING SURVIVABLE. A colleague's note arrives in the merged RECORD. If I
     hold no overlay entry for that action - true for any action I have not opened - `actField`
     falls back to the record and the note DISPLAYS correctly, so nothing looks wrong. Then
     `_bakeActionsFor` creates the missing entry through this function, gets `updates: []`, and
     writes that over the note on my next Save. The note is destroyed by the act of saving
     something else, having been visible on screen the whole time.
     Found by the W4.2 smoke walking the journey end to end, with the battery and every
     acceptance probe already green - the defect lives in the gap between "the app shows it" and
     "the app saves it", and only a journey that does both can see it.
     Cloned per note so the overlay never aliases the record's array. */
  if (!e.updates) e.updates = Array.isArray(a.updates)
    ? a.updates.map(function (u) { return Object.assign({}, u); })
    : [];
  /* v2.11: backfill stable noteId on any existing notes that don't have one
     (older saved files). Notes added going forward get noteId at creation time. */
  const _noteIds = new Set(e.updates.map(u => u && u.noteId).filter(Boolean));
  e.updates.forEach((u, i) => {
    if (!u.noteId) u.noteId = _claimUniqueNoteId(_legacyNoteId(a.id, i), _noteIds);
  });
}
/* ── v13.1 (A06): ONE fallback semantic for the four editable action fields ─────
   desc / owner / source / date were read as `e.x || a.x` in the renderer, the save bake
   and the snapshot. An empty string is falsy, so a value the user CLEARED fell back to
   the stored one: the field blanked for an instant, the card re-rendered, and the old
   value was back. Save then wrote the old value too. An explicitly set edit now wins,
   including '' — the semantic `due` has always had.
   ⚠ initActionEdits() seeds every one of these keys from the action, so `undefined`
   here means "this edits record has never held the key", never "the user cleared it".
   ⚠ Hoisted `function` on purpose: the boot render calls it before this line (§3).
   Every read of the pair goes through here so the sites cannot drift apart again. */
function actField(e, a, k) {
  if (e && e[k] !== undefined) return e[k];
  return a ? a[k] : undefined;
}
let edits = {};
ACTIONS.forEach(initActionEdits);
/* v2.8: one-time cleanup of orphan edits — keys with no matching action.
   Cleans state from the moment the file loads so no stale data lingers. */
(function pruneOrphanEditsOnLoad() {
  const validIds = new Set(ACTIONS.map(a => a.id));
  Object.keys(edits).forEach(k => { if (!validIds.has(Number(k))) delete edits[k]; });
})();

let newActions = [];
let nextId = ACTIONS.length ? Math.max(...ACTIONS.map(a=>a.id)) + 1 : 1;
let nextSessionId = SESSIONS.length ? Math.max(...SESSIONS.map(s => s.id)) + 1 : 1;
/* ── ⚠⚠ v16.8 (W5): AN ACTION OR MEETING NUMBER IS NEVER REUSED EITHER ─────────────
   SWEEP-2 (F-P4c-1) measured all nine registers against the two safeguards this app already
   has, and found six protected by a persisted high-water mark and three not: actions,
   meetings and workstreams. Deleting the highest-numbered record freed its id at the next
   reseed — and `_reseedMintCounters` runs on EVERY project switch.
   ⚠⚠ THE OLD JUSTIFICATION WAS THAT REUSE IS INERT HERE, AND THAT IS NOT WHY IT WAS
   SURVIVABLE. v10.3 (W4) recorded the v8.2 audit's verdict as "id reuse is inert — nothing
   references these ids. True for actions/sessions/workstreams; demonstrably FALSE for
   stakeholders". ⚠ Actions ARE referenced — by `dependsOn`, by `relatedTo`, by a workstream's
   `actionIds`, by `appMeta.focusActionId`, and by the `edits` overlay's own keys; workstreams
   are referenced by `parentId` and by every action assigned to them. What made reuse
   survivable was that all of those are PRUNED when the target is deleted (SWEEP-2 P4c's
   pattern B), which is a different guarantee and a weaker one: it holds only as long as every
   future reference remembers to prune. **The benefit → project link is what happens when one
   forgets** — it had neither safeguard, and it silently retargeted.
   ⚠⚠ SCHEMA-CONSENT 2026-08-27 — `appMeta.actSeq` and `appMeta.mtgSeq`, asked for and given
   separately from the wave's Go. His reasoning, recorded: *"It's about being concerned that
   stuff falls apart which isn't the case here."*
   ⚠ LAZY, like all six before them. */
function _nextActSeq() {
  const id = nextId++;
  appMeta.actSeq = id;
  return id;
}
function _nextMtgSeq() {
  const id = nextSessionId++;
  appMeta.mtgSeq = id;
  return id;
}
/* v2.19 item 8: ensure appMeta has focusActionId field (additive — missing → null).
   Also orphan-strip: if focusActionId references a deleted/missing action, clear it.
   Runs AFTER newActions declaration so spread is safe. */
if (!('focusActionId' in appMeta)) appMeta.focusActionId = null;
if (appMeta.focusActionId != null) {
  const _focusValidIds = new Set([...ACTIONS, ...newActions].map(a => a.id));
  if (!_focusValidIds.has(appMeta.focusActionId)) appMeta.focusActionId = null;
}
/* v2.21: orphan-strip dependsOn and relatedTo arrays — remove any IDs that
   no longer correspond to existing actions (deleted-then-saved scenarios).
   Self-references are also stripped defensively. */
{
  const _validIds = new Set([...ACTIONS, ...newActions].map(a => a.id));
  Object.keys(edits).forEach(idStr => {
    const id = Number(idStr);
    const e = edits[id];
    if (Array.isArray(e.dependsOn)) {
      e.dependsOn = e.dependsOn.filter(x => _validIds.has(x) && x !== id);
    }
    if (Array.isArray(e.relatedTo)) {
      e.relatedTo = e.relatedTo.filter(x => _validIds.has(x) && x !== id);
    }
  });
}

let activeStatus='all', activePriority='all', searchQuery='', sortBy='id';
/* v14.2 (d): 'status' | 'owner'. Persists for free - it rides sortBy's path through
   captureUIState/restoreUIState, which is already per-project. */
let groupBy='status';
/* v4.0: Actions view ('list' | 'board') — session-only, defaults to List (no data change). */
let actionsView = 'list';
/* v2.10: action sort direction. false = ascending (natural order — IDs lowest first,
   dates oldest first, priority High first, status Active first). */
let sortDesc = false;
/* v2.9: track whether file has unsaved changes — drives beforeunload prompt.
   Set true by data mutations (markDirty), reset false on save. */
let isDirty = false;
/* v2.90: bootCompleted gates markDirty() calls during the init sequence.
   Some Chrome browser quirks (contenteditable blur, autofill, etc.) can fire
   markDirty() before the user has interacted with anything. This flag prevents
   the save-button .dirty indicator from blinking on file open.
   Set true at the end of the boot sequence (last line of script). */
let bootCompleted = false;
/* v7: two "Save all" controls exist now — the project-header button (#saveBtn) and a
   global button in the shared landing bar (#saveBtnGlobal). Both carry class .save-btn;
   every dirty/saved visual update fans out across ALL of them so their state can't
   diverge. Presentation only — the Save routine (exportFile) is unchanged and still
   writes the WHOLE DB in one click, from whichever button. */
const _SAVE_IDLE_SVG = '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.3"><path d="M3 2.5h5.8L11.5 5.2V11a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5Z"></path><path d="M4.7 2.5v2.4h3.4V2.5"></path><path d="M4.7 8.1h4.6v3.4H4.7Z"></path></svg>';
const _SAVE_DONE_SVG = '<svg viewBox="0 0 13 13" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2 7l3 3 6-6"></path></svg>';
const _SAVE_IDLE_HTML = _SAVE_IDLE_SVG + ' Save all';
const _SAVE_DONE_HTML = _SAVE_DONE_SVG + ' Saved';
/* v10.4.2 (W3): the SAVING state. Deliberately says "Saving…", never "Saved" — the whole
   point of v10.3's change was that the button must not claim success before the bytes are
   on disk. This restores immediate feedback without restoring the false claim. */
const _SAVE_BUSY_SVG = '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M7 1.6a5.4 5.4 0 1 1-5.4 5.4" stroke-linecap="round"></path></svg>';
const _SAVE_BUSY_HTML = _SAVE_BUSY_SVG + ' Saving…';
function _allSaveBtns() { return document.querySelectorAll('.save-btn'); }
/* ⚠ v13.1 (A28): the LABEL is part of this state too. _setSaveSaved() writes "✓ Saved" into
   the button and nothing ever put it back, so the next edit added the amber unsaved dot
   beside a button still claiming the file was saved — two opposite answers, one control.
   Only rewritten when it actually differs: since A12 this runs on every keystroke. */
function _setSaveDirty() { _allSaveBtns().forEach(b => { b.classList.remove('saved', 'saving'); b.classList.add('dirty'); if (b.innerHTML !== _SAVE_IDLE_HTML) b.innerHTML = _SAVE_IDLE_HTML; }); }
function _setSaveSaved() { _allSaveBtns().forEach(b => { b.classList.remove('dirty', 'saving'); b.classList.add('saved'); b.innerHTML = _SAVE_DONE_HTML; }); }
function _setSaveIdle()  { _allSaveBtns().forEach(b => { b.classList.remove('saved', 'dirty', 'saving'); b.innerHTML = _SAVE_IDLE_HTML; }); }
function _setSaveSaving() { _allSaveBtns().forEach(b => { b.classList.remove('saved'); b.classList.add('saving'); b.innerHTML = _SAVE_BUSY_HTML; }); }
/* ── v13.1 (A64): Save re-entrancy. True from the moment exportFile commits to writing
   until its completion settles, so a double-click cannot start a second overlapping write
   with its own savedAt stamp.
   ⚠⚠ v17.3 (W1, F-P1-02): THIS COMMENT USED TO END "Cleared in a .finally so a throw cannot
   strand it", AND THAT WAS FALSE FOR EVERY SYNCHRONOUS THROW. The .finally hangs off the
   _writeDataFile promise, so a throw in the preparation — _captureAllFromDOM, _stampSaveTime,
   _buildActionObjects, _revPlan, _buildSaveDB or serializeDB — never created the promise and
   never reached any clear site. Save then returned early at the guard below for the rest of
   the session, writing nothing and saying nothing. Measured by the §v17.3 QA run (M1-20) at
   three injection points; serializeDB's v16.1 (d) identity refusal is a DESIGNED throw that
   lands squarely in that block. The exportFile wrapper below is the clear site that covers it.
   ⚠ A comment claiming total coverage above a partial guard is exactly what let v16.1 (d)'s
   class survive three versions — so this one says what is actually covered. */
let _saveInFlight = false;
/* ── v17.3 (W1): the stamp a failed save has to roll back ──────────────────────────
   _stampSaveTime moves savedAt and appMeta.lastSaved forward IN MEMORY before the bytes are
   built, and the write-failure branch already rolls that back with _unstampSaveTime. A
   synchronous throw has to do the same, or the header advertises an "As of" for a save that
   never happened — so the pair is parked here where the wrapper's catch can reach it. Null
   whenever no save is mid-flight; cleared in the same .finally that clears _saveInFlight. */
let _pendingSaveStamp = null;
/* ── v17.4 (W3, F-B-04): a successful atomic write can still be followed by a failed
   in-memory completion. That is NOT an unsaved file, but this tab may now hold a mixture of
   pre- and post-commit state. Keep the guard session-only (never serialised), preserve any
   newer edit behind beforeunload, and require a fresh reload instead of offering a retry from
   state we cannot prove coherent. Object shape: {savedAt, reason}; false on every fresh page. */
let _saveNeedsReload = false;
/* ── v13.1 (A05): a monotonic edit counter. exportFile snapshots it immediately after the
   capture that freezes the bytes, and refuses to clear the dirty flag on success if it has
   moved — an edit made DURING the write is not in the bytes on disk, and saying otherwise
   is how work disappears on tab close with no prompt. A counter, not a boolean, because
   isDirty is cleared by the save itself. */
let _editSeq = 0;
function markDirty() {
  if (!bootCompleted) return;  /* v2.90: ignore markDirty calls during boot */
  isDirty = true;
  _editSeq++;
  /* v2.40: visible unsaved-changes indicator (amber dot) — on every Save-all button. */
  _setSaveDirty();
}

/* ── v13.1 (A12): the tab-close warning, restored for the blur-only editors ────────
   ~27 editors in this file commit on BLUR — 18 registered in JS, 9 as inline onblur= in
   markup — and markDirty() lives INSIDE each commit handler. On the Save path that is safe
   BY ACCIDENT: clicking a button blurs the focused element first. Closing the TAB is the
   unguarded case — beforeunload fires before any blur, so isDirty is still false, no prompt
   appears and the text is gone. _captureAllFromDOM()'s comment claims it "pulls everything
   the user has typed but not yet blurred into the model"; it covers three surfaces, none of
   the two dozen editors added since v8.
   The fix only DIRTIES. It never writes to the model, so commit semantics are untouched and
   the blur handler stays the single writer. */

/* Register a blur commit and the input listener that goes with it, together, so the two can
   never end up on different elements. Every `X.addEventListener('blur', ...)` in this file
   goes through here — that is the point: a new editor gets the warning for free. */
function _onBlurCommit(el, commit) {
  if (!el) return;
  el.addEventListener('blur', commit);
  el.addEventListener('input', _markDirtyOnly);
}
function _markDirtyOnly() { markDirty(); }

/* The inline-markup half. `input` bubbles — including from contenteditable — so ONE
   delegated listener covers every editor carrying an inline onblur=, and every future one
   for free, which is how this defect reached 27 sites unnoticed.
   ⚠ The predicate is a POSITIVE test, deliberately not "dirty on any keystroke": the search
   and filter boxes are not model edits and must not arm the leave-site prompt.
   Census of this shell: 9 inline onblur= editors; 6 FREE-TEXT onchange= editors (the action
   Owner / Requested by / Source / Title / Details fields and the attendee Organisation),
   where typing is equally uncommitted until focus leaves; and 26 other onchange= controls —
   checkboxes, selects, dates, times, a number and a file picker — which commit the instant
   they change. Dirtying THOSE on input would mark the file dirty for ticking an export
   checkbox, so the tag/type test excludes them. Re-run the census if the ratio changes. */
function _commitsOnBlur(t) {
  if (!t || !t.tagName) return false;
  if (t.onblur) return true;
  /* v15.2: the CI capture form commits on its Add button, not on blur — but a half-typed
     draft is exactly what this listener exists to protect. Typing in it now arms the dirty
     flag and the leave-site prompt: the same trade A12 made everywhere (dirty may be armed
     for a draft later cancelled; the alternative was a paragraph lost to a closed tab, the
     v15.1 (b) class one register over). */
  if (t.closest && t.closest('#ciForm')) return true;
  if (!t.onchange) return false;
  return t.tagName === 'TEXTAREA' ||
         (t.tagName === 'INPUT' && (t.type === 'text' || t.type === ''));
}
document.addEventListener('input', function (ev) {
  if (_commitsOnBlur(ev.target)) markDirty();
});
/* v2.7: hide-completed toggle — session-state only (NOT persisted) */
let hideCompleted = false;
/* v2.5: ids of actions edited or annotated this session — drives the yellow .has-update
   highlight, which now clears on save+reload (session-local, not persisted). */
const sessionEditedActionIds = new Set();
/* v2.18.1: in-memory draft text for the per-action update textareas. Captured
   before each render and restored after, so re-renders within a browser session
   don\'t silently discard text that the user has typed but not yet clicked Add.
   NOT persisted to file — drafts are session-local; closing the tab discards them. */
const pendingUpdates = {};

function captureUpdateDrafts() {
  // Read every visible per-action update textarea and store its current value.
  // Empty values clear the entry (no need to restore "").
  document.querySelectorAll('textarea.update-textarea[id^="ta-"]').forEach(ta => {
    const id = Number(ta.id.slice(3));
    if (!Number.isFinite(id)) return;
    if (ta.value) pendingUpdates[id] = ta.value;
    else delete pendingUpdates[id];
  });
}

/* ══ v14.1 (a): IS THERE AN UNCOMMITTED UPDATE NOTE ON SCREEN? ═══════════════════════════
   The `ta-<id>` textarea is committed by the Add button, not by blur, and Save never
   serialises its draft. That was RIGHT in v13.1 and is recorded as such: marking the document
   dirty on input would let Save clear the dirty flag while the draft was still uncommitted,
   telling the user their text was safe when it was not.
   ⚠ v14 turned that exemption into the exposure. A CLEAN tab is exactly the state in which
   ◆9's watcher silently re-merges and `_adoptDB` re-renders — and the re-render runs
   `setActiveProject`, which deliberately BLANKS every update textarea. So the sequence became:
   type a note, pause, a colleague saves, and the half-typed note leaves the screen with no
   action from the user at all.
   ⚠ THIS IS THE SMALL FIX, AND DELIBERATELY NOT THE OBVIOUS ONE. Making drafts survive
   `setActiveProject` would resurrect the cross-project draft bleed that the blanking exists to
   prevent — project A's sentence appearing on project B's action card, proven to survive
   clearing the map alone. Instead a pending draft simply BLOCKS the silent re-merge, reusing
   the invariant `isDirty` already carries, and the folder-changed notice is raised instead.
   ⚠ READ THE LIVE TEXTAREAS, NOT `pendingUpdates`. The map is only filled by
   captureUpdateDrafts() at render time, so text typed since the last render is not in it yet —
   and that is precisely the text at risk. Reading the DOM also means a draft cannot outlive its
   textarea, so a stale map entry can never wedge the refresh off permanently. */
function _hasPendingUpdateDraft() {
  const tas = document.querySelectorAll('textarea.update-textarea[id^="ta-"]');
  for (let i = 0; i < tas.length; i++) {
    if (tas[i] && typeof tas[i].value === 'string' && tas[i].value.trim() !== '') return true;
  }
  return false;
}

function restoreUpdateDrafts() {
  // Called immediately after render() rebuilds the list. Inject any captured
  // drafts back into the freshly-rendered textareas.
  Object.keys(pendingUpdates).forEach(idStr => {
    const ta = document.getElementById('ta-' + idStr);
    if (ta) ta.value = pendingUpdates[idStr];
  });
}

/* v2.19 item 8: focus marker. Singleton — at most one action focused at a time.
   The focused action floats to the top of the list regardless of sort.
   Stored in appMeta.focusActionId so it round-trips through save+reload. */
/* v2.22: agenda-flag icon — teal checkbox style, hover-only when inactive */
/* v2.93: bookmark icon replaces the tickbox.
   Stroke/fill controlled by CSS based on .flagged class — when flagged,
   the path is filled with teal; when not, it shows just the outline. */
const AGENDA_FLAG_SVG = '<svg viewBox="0 0 18 18"><path d="M4 2h10v14l-5-3.5L4 16V2z"/></svg>';

const FOCUS_CROSSHAIR_SVG = '<svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><circle cx="9" cy="9" r="7"/><line x1="9" y1="0.5" x2="9" y2="3.5"/><line x1="9" y1="14.5" x2="9" y2="17.5"/><line x1="0.5" y1="9" x2="3.5" y2="9"/><line x1="14.5" y1="9" x2="17.5" y2="9"/><circle cx="9" cy="9" r="2.3" fill="currentColor" stroke="none"/></svg>';
/* v2.22: toggle on-the-agenda flag for an action.
   Flagged cards float above unflagged in render order (within each tier,
   the active sort chain still applies). Focus crosshair still wins
   absolute pos 0 above all flagged cards. */
function toggleFlag(id) {
  edits[id].flagged = !edits[id].flagged;
  sessionEditedActionIds.add(id);
  markDirty();
  refreshAll();
}

function toggleFocus(id) {
  const cur = appMeta.focusActionId;
  appMeta.focusActionId = (cur === id) ? null : id;
  markDirty();
  refreshAll();
}
// Tracks which stat card is highlighted as the active shortcut filter — purely a visual marker;
// the actual filtering is driven by activeStatus + activePriority below.
let activeStatFilter='all';
let activeTab = 'dashboard';  /* v3.0: Dashboard is the new landing tab (was 'actions'/'notes' in v2.x).
                                 restoreUIState() will override this if a saved activeTab exists. */

/* ── UTILS ── */
const PRIORITY_ORDER = {High:0,Medium:1,Low:2};
const STATUS_ORDER = {"In progress":0,"Not yet started":1,"On hold":2,"Completed":3,"Cancelled":4};
const statusKey = s => s==="Not yet started"?"not-started":s==="In progress"?"in-progress":s==="On hold"?"on-hold":s==="Completed"?"completed":s==="Cancelled"?"cancelled":"unknown";
const statusLabel = s => s==="Not yet started"?"Not started":s;
function nowStamp() { const n=new Date(); return n.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})+' · '+n.toLocaleDateString('en-GB',{day:'2-digit',month:'short'}); }
/* v10.3 (W4): was `_todayISO()` — UTC, so it named the wrong day
   between local midnight and the UTC offset (up to 02:00 here in summer). Delegates to the
   local-date helper so the whole app agrees on what "today" is; two near-identically named
   helpers with different timezone semantics is exactly how the Focus off-by-one survived. */
function todayISO() { return _todayISO(); }
/* ── v13.1 (A34 + A58): two one-line accessibility primitives ────────────────────────
   _keyActivate — a <span onclick> is a button to a mouse and nothing at all to a keyboard:
   it is not in the tab order, Enter and Space do nothing, and a screen reader announces
   plain text. Adding role="button" and tabindex="0" is only two thirds of the fix — the
   role PROMISES Enter/Space, and this supplies them. Space is preventDefault'ed so the
   page does not scroll under the user.
   _setSelected — writes the selected CLASS and aria-pressed together, from one argument,
   so the paint and the announced state cannot drift. That is the whole reason it exists
   rather than a bare setAttribute beside each existing toggle. */
function _keyActivate(ev) {
  if (ev.key !== 'Enter' && ev.key !== ' ' && ev.key !== 'Spacebar') return;
  ev.preventDefault();
  ev.currentTarget.click();
}
function _setSelected(el, on, cls) {
  if (!el) return;
  el.classList.toggle(cls || 'active', !!on);
  el.setAttribute('aria-pressed', on ? 'true' : 'false');
}
/* v8.2.3 (Q8): single HTML escaper. esc now delegates to _escapeHtml (the canonical
   implementation below) so a literal 0 / false is rendered rather than swallowed — only
   null / undefined become ''. Both names are kept (72 esc + 41 _escapeHtml call sites)
   and both remain hoisted function declarations, since the boot render runs before the
   _escapeHtml definition further down the file. */
function esc(s) { return _escapeHtml(s); }

/* ── TABS ── */
/* v2.19 item 6: per-tab scroll position memory. Not persisted across sessions. */
const tabScrollPositions = {};
function switchTab(tab) {
  /* v8.5 (IA regroup): RACI and Minutes are no longer tabs — they're sections inside
     People (id 'stakeholders') and Governance (id 'changes'). Old ids can still arrive
     from a saved uiState.activeTab or legacy callers; map them to their new homes. */
  if (tab === 'raci') tab = 'stakeholders';
  if (tab === 'notes') tab = 'changes';
  if (!_progressPermitTab(tab)) return false;
  if (tab !== activeTab) _disposeActionLabels();
  if (tab !== 'stakeholders') {
    _settleStakeholderCards();
    _closeStakeholderMenus();
  }
  syncSessionsFromDOM();  // capture any pending input before tab change (esp. leaving minutes)
  /* v2.19: snapshot current tab's scroll before switching away */
  if (activeTab) tabScrollPositions[activeTab] = window.scrollY;
  activeTab = tab;
  if (_wbsReorderReady) {
    const uid = _wsActiveProjectUid() || '';
    if (tab === 'scope') _wsReorderBind(uid);
    else if (_reorderUiMatches('workstream', uid)) _reorderUiReset();
    _wsReorderSyncUi(uid);
  }
  /* v8.5: 6-area routing (IA regroup) — Overview ('dashboard') · Scope · Actions ·
     People ('stakeholders') · RAID ('risks') · Governance ('changes'). Internal ids keep
     their legacy names (house precedent: tab-risks has shown "RAID" since v8.4). */
  const allTabs = ['dashboard', 'scope', 'actions', 'stakeholders', 'risks', 'changes'];
  allTabs.forEach(t => {
    const panel = document.getElementById('panel-' + t);
    const btn = document.getElementById('tab-' + t);
    if (panel) panel.classList.toggle('active', tab === t);
    _setSelected(btn, tab === t);   /* v13.1 (A58) */
  });
  document.getElementById('actionsControls').style.display = tab==='actions' ? 'flex' : 'none';
  /* v13.1 (A59): renderStakeholders() and renderMendelowGrid() are the SAME function — both
     are one-line delegates to renderStakeholderView() — so calling the historical pair
     rendered the whole People view, its stat strip, its role-type filter and three datalists
     TWICE for one gesture. Both names are kept (~20 sites call one or the other), but the
     two places that called BOTH now call it once. */
  if (tab==='stakeholders') { renderStakeholderView(); renderRaci(); renderComms(); }   /* v8.6: + comms plan section */
  if (tab==='risks') renderRaid();
  if (tab==='changes') { renderChangeRequests(); renderSessions(); renderKpis(); renderImprovements(); }   /* v8.8: + KPIs section (hints recompute on entry) · v14.4 (a): + continuous improvement */
  /* v2.19: restore target tab's saved scroll (no smooth — should feel like an instant teleport) */
  const saved = tabScrollPositions[tab] || 0;
  window.scrollTo({ top: saved, behavior: 'instant' });
  return true;
}

/* ──────────────────────────────────────────────────────────────────────
   v3.0 — STAKEHOLDERS tab logic
   Pure additive code block. No interaction with ACTIONS/SESSIONS/edits.
   ────────────────────────────────────────────────────────────────────── */

const ENGAGEMENT_STATES = ['Unaware', 'Resistant', 'Neutral', 'Supportive', 'Leading'];
const STK_NOTES_PLACEHOLDER = 'Key expectations / requirements, concerns / risks, engagement strategy…';

/* v5.1 §8: PMI-complete stakeholder ROLE/TYPE — a controlled, multi-select list.
   STRUCTURAL/FUNCTIONAL only; attitude (Champion/Blocker/…) is NOT here — it lives in
   the engagement field (Unaware…Leading), so the two can never contradict.
   Stored per-stakeholder as additive `roleTypes[]`, default absent → reads as none.
   The code never writes an empty array (kept clean for lossless round-trip). */
const STK_ROLE_TYPES = [
  { group: 'Sponsorship & governance', items: ['Sponsor', 'Executive / Senior leadership', 'Steering committee / Board', 'PMO', 'Business owner'] },
  /* v14.1 (e): Programme manager and Product manager, his call, beside Project manager.
     ⚠ BRITISH SPELLING, per the convention this whole app follows - and it is a PROGRAMME
     management tool, so 'Program manager' would be the one place it said otherwise.
     ⚠ ADDITIVE ONLY. roleTypes[] stores the LABEL STRING itself, so an entry renamed here
     is orphaned on every record already carrying it. Adding is free; renaming is a
     migration. */
  { group: 'Delivery',                 items: ['Programme manager', 'Project manager', 'Product manager', 'Project team', 'Functional / Line manager'] },
  { group: 'Business & users',         items: ['Customer / Client', 'End user', 'Operations / Business unit'] },
  { group: 'External',                 items: ['Vendor / Supplier / Contractor', 'Business partner', 'Regulator / Authority'] },
  { group: 'Specialist',               items: ['Subject-matter expert (SME) / Consultant'] },
  { group: 'Other',                    items: ['Other'] },
];
const STK_ROLE_TYPE_FLAT = STK_ROLE_TYPES.reduce((a, g) => a.concat(g.items), []);
let activeRoleTypeFilter = null; /* null = no role-type filter; else one role string */

/* ── v5.2: Power/Interest are continuous integers 0–100 (migrated from the old
   L/M/H enum). These helpers are legacy-safe — a stray 'low'|'medium'|'high' still
   reads correctly — so nothing breaks if a value hasn't been migrated yet. */
const _PI_FROM_LEVEL = { low: 25, medium: 50, high: 75 };
function stkPI(s, field) {
  const v = s ? s[field] : undefined;
  if (typeof v === 'number' && isFinite(v)) return Math.max(0, Math.min(100, Math.round(v)));
  if (typeof v === 'string' && _PI_FROM_LEVEL[v] != null) return _PI_FROM_LEVEL[v];
  return 50;
}
function stkLevel(v) { return v >= 67 ? 'High' : (v >= 34 ? 'Med' : 'Low'); }   // numeric → label
/* Mendelow strategy from continuous power/interest at the 50 midpoint, mapped to the
   PMP palette (Manage=red, Keep satisfied=amber, Keep informed=green, Monitor=grey).
   Derived — never stored. */
function stkStrategy(s) {
  const p = stkPI(s, 'power'), n = stkPI(s, 'interest');
  const hi = p >= 50, ni = n >= 50;
  if (hi && ni)  return { key:'mc', label:'Manage closely', c:'--high',   bg:'--high-bg',   bd:'--high-border' };
  if (hi && !ni) return { key:'ks', label:'Keep satisfied', c:'--medium', bg:'--medium-bg', bd:'--medium-border' };
  if (!hi && ni) return { key:'ki', label:'Keep informed',  c:'--low',    bg:'--low-bg',    bd:'--low-border' };
  return                { key:'mo', label:'Monitor',        c:'--text3',  bg:'--surface2',  bd:'--border' };
}

/* v3.1: filter by stakeholderSearchQuery (case-insensitive substring across
   name/role/company/notes). Returns the list of stakeholders to display
   given the current search state. */
function getFilteredStakeholders() {
  let list = STAKEHOLDERS.slice();
  /* v4.0: tab-wide category filter — the pills at the top of the Stakeholders
     tab narrow BOTH the cards and the Power/Interest grid (both consume this
     list). A category not currently active is dropped here, not CSS-hidden.
     Missing/unknown category is treated as 'Customer' (the load-time default). */
  if (activeGridCategories.size < STK_CATEGORIES.length) {
    list = list.filter(s => {
      const c = (typeof s.category === 'string' && STK_CATEGORIES.includes(s.category)) ? s.category : 'Customer';
      return activeGridCategories.has(c);
    });
  }
  if (activeRoleTypeFilter) {
    list = list.filter(s => Array.isArray(s.roleTypes) && s.roleTypes.includes(activeRoleTypeFilter));
  }
  if (stakeholderSearchQuery) {
    const q = stakeholderSearchQuery.toLowerCase();
    list = list.filter(s => {
      /* v15.2: + location — v14.1 (c) wired its HIGHLIGHT selector and forgot the hay, so
         searching "Munich" hid everyone based there unless the word appeared elsewhere. */
      const hay = [s.name || '', s.role || '', s.organisation || '', s.location || '', s.notes || ''].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }
  return list;
}

/* v3.1: sort by configured field + direction. Mutates a copy, returns it.
   Secondary sort by Name — ALWAYS ascending, regardless of primary
   direction. Names within a tied group always read alphabetically,
   which is the conventional expectation (e.g. when sorting by Power
   desc with two H ties, you get Anna then Stefan, not Stefan then Anna).
   v13.1 (U02): the `_LEVEL_RANK` map and the sentence claiming Power and Interest
   map to 1/2/3 are gone — both were retired when stkPI() moved the axes to the
   continuous 0-100 scale, which is what the sort below actually reads. */
function applySortToStakeholders(arr) {
  const field = appMeta.stakeholderSort.field;
  const dir = appMeta.stakeholderSort.dir;
  const dirMul = (dir === 'desc') ? -1 : 1;
  const indexed = arr.map((s, i) => ({ s, i }));
  indexed.sort((a, b) => {
    let primary = 0;
    if (field === 'name') {
      const an = (a.s.name || '').trim();
      const bn = (b.s.name || '').trim();
      if (!an && bn) primary = 1;
      else if (an && !bn) primary = -1;
      else if (!an && !bn) primary = 0;
      else primary = an.localeCompare(bn);
    } else {
      /* field is 'power' or 'interest' — now continuous 0–100 (legacy-safe via stkPI) */
      primary = stkPI(a.s, field) - stkPI(b.s, field);
    }
    primary *= dirMul;
    if (primary !== 0) return primary;
    /* Secondary (always ascending): Name. Tertiary: insertion order. */
    const an = (a.s.name || '').trim();
    const bn = (b.s.name || '').trim();
    const nameCmp = an.localeCompare(bn);
    if (nameCmp !== 0) return nameCmp;
    return a.i - b.i;
  });
  return indexed.map(x => x.s);
}

/* v3.0 legacy entry point — kept for code that might call it directly.
   Now applies filter + sort together. The cards render and the Mendelow
   grid both consume this same list so search narrows BOTH the list AND
   the grid. */
function getSortedStakeholders() {
  return applySortToStakeholders(getFilteredStakeholders());
}

/* v17.6: numeric stakeholder IDs are project-local. Retained DOM must also belong to
   this project UID, collection and original record object, not a replacement with its ID. */
function _stakeholderProjectUid() {
  const p = ((window.__DB__ || {}).projects || []).find(p => p && p.id === activeProjectId);
  return p && p.uid || '';
}
function _stakeholderOwner(record, surface) {
  return { uid: _stakeholderProjectUid(), records: STAKEHOLDERS,
    record, id: record && record.id, surface };
}
function _stakeholderOwnerCurrent(owner, visible) {
  return !!(owner && owner.uid && owner.uid === _stakeholderProjectUid()
    && owner.records === STAKEHOLDERS && owner.record && owner.record.id === owner.id
    && STAKEHOLDERS.includes(owner.record) && owner.surface && owner.surface.isConnected
    && (!visible || (owner.surface.getClientRects().length
      && getComputedStyle(owner.surface).visibility === 'visible')));
}
function _clearStakeholderCards() {
  const grid = document.getElementById('stkGrid');
  if (grid) { grid._stkCapture = null; grid.replaceChildren(); }
}
function _captureStakeholderCards() {
  const grid = document.getElementById('stkGrid');
  const ctx = grid && grid._stkCapture;
  /* Being off the People tab or in a collapsed section does not invalidate a draft. */
  if (!ctx || stkView() !== 'cards' || ctx.uid !== _stakeholderProjectUid()
      || ctx.records !== STAKEHOLDERS) return;
  grid.querySelectorAll('.stk-card').forEach(card => {
    const owner = ctx.cards.get(card);
    if (!_stakeholderOwnerCurrent(owner, false) || card.parentNode !== grid
        || Number(card.dataset.id) !== owner.id) return;
    const s = owner.record;
    const ta = card.querySelector('.stk-notes');
    if (ta) { s.notes = ta.value || ''; ta.textContent = ta.value; }
    const name = card.querySelector('.stk-name');
    const role = card.querySelector('.stk-role');
    const organisation = card.querySelector('.stk-organisation');
    if (name) s.name = (name.textContent || '').trim();
    if (role) s.role = (role.textContent || '').trim();
    if (organisation) s.organisation = ((organisation.value !== undefined
      ? organisation.value : organisation.textContent) || '').trim();
  });
}
function _settleStakeholderCards() {
  const focused = document.activeElement;
  if (focused && focused.closest('#stkGrid') && typeof focused.blur === 'function') focused.blur();
  _captureStakeholderCards();
}

function _renderStakeholderCards() {
  _closeStakeholderMenus();
  const grid = document.getElementById('stkGrid');
  const empty = document.getElementById('stkEmpty');
  const emptyFiltered = document.getElementById('stkEmptyFiltered');
  if (!grid) return;
  const list = getSortedStakeholders();
  _clearStakeholderCards();
  const ctx = { uid: _stakeholderProjectUid(), records: STAKEHOLDERS, cards: new Map() };
  grid._stkCapture = ctx;
  if (!list.length) {
    /* v3.1: distinguish "no stakeholders yet" from "search matched nothing" */
    if (STAKEHOLDERS.length === 0) {
      if (empty) empty.style.display = '';
      if (emptyFiltered) emptyFiltered.style.display = 'none';
    } else {
      if (empty) empty.style.display = 'none';
      if (emptyFiltered) emptyFiltered.style.display = '';
    }
    return;
  }
  if (empty) empty.style.display = 'none';
  if (emptyFiltered) emptyFiltered.style.display = 'none';

  list.forEach(s => {
    const card = buildStakeholderCard(s);
    ctx.cards.set(card, _stakeholderOwner(s, card));
    grid.appendChild(card);
  });

  /* v3.0.2 highlight: reuse the cross-tab highlight machinery to mark
     matched substrings inside the rendered stakeholder cards.
     Selectors target the static-text display elements (the editable
     contenteditable fields are skipped while focused). */
  if (typeof highlightSearchMatches === 'function' && stakeholderSearchQuery) {
    highlightSearchMatches(grid, stakeholderSearchQuery, _STAKEHOLDER_HIGHLIGHT_SELECTORS);
  }
}

/* v3.1: selectors used by the v3.0.2 highlight engine for stakeholder cards. */
const _STAKEHOLDER_HIGHLIGHT_SELECTORS = [
  '.stk-name',
  '.stk-role',
  '.stk-organisation',
  '.stk-location',
  /* .stk-notes is a <textarea> — form fields can't host inline HTML.
     Filter still catches notes matches at the card level (the card stays
     visible when notes match), but the yellow highlight can't be drawn
     inside the textarea. This is a browser-level limitation, not a code
     limitation — same as on the Minutes tab. */
];


/* v5.2: derived strategy chip for a card (read-only; refreshed live as sliders move). */
function buildStrategyChip(s) {
  const st = stkStrategy(s);
  const chip = document.createElement('span');
  chip.className = 'stk-strat-chip';
  chip.title = 'Strategy — derived from influence × interest';
  _paintStrategyChip(chip, st);
  return chip;
}
function _paintStrategyChip(chip, st) {
  chip.textContent = st.label;
  chip.style.color = `var(${st.c})`;
  chip.style.background = `var(${st.bg})`;
  chip.style.borderColor = `var(${st.bd})`;
}

/* v5.2: inline Influence/Interest slider — the bar IS the control. Drag (or use the
   L/M/H presets) to set 0–100; the fill + level update live, and onChange persists.
   onChange(callback) refreshes the card's derived strategy chip + the stat strip +
   (if visible) the matrix — without rebuilding the card, so the drag stays smooth. */
function buildPISlider(s, field, onChange, hideLabel) {
  const cat = STK_CATEGORIES.includes(s.category) ? s.category : 'Customer';
  const fill = `var(--cat-${cat.toLowerCase()})`;   // fill stays category-stable while dragging
  const row = document.createElement('div');
  row.className = 'stk-slider-row';

  const label = document.createElement('span');
  label.className = 'stk-slider-label';
  label.textContent = (field === 'power') ? 'Influence' : 'Interest';

  const slider = document.createElement('input');
  slider.type = 'range'; slider.min = '0'; slider.max = '100'; slider.step = '1';
  slider.className = 'stk-slider';
  slider.value = String(stkPI(s, field));
  slider.setAttribute('aria-label', (field === 'power' ? 'Influence' : 'Interest') + ' for ' + (s.name || 'stakeholder'));

  const lvl = document.createElement('span');
  lvl.className = 'stk-slider-lvl';

  const paint = () => {
    const v = parseInt(slider.value, 10) || 0;
    slider.style.background = `linear-gradient(to right, ${fill} ${v}%, var(--surface2) ${v}%)`;
    lvl.textContent = stkLevel(v);
  };
  const setVal = (v, persist) => {
    v = Math.max(0, Math.min(100, Math.round(v)));
    slider.value = String(v);
    s[field] = v;
    paint();
    if (typeof onChange === 'function') onChange();
    if (persist) markDirty();
  };
  slider.oninput = () => { s[field] = parseInt(slider.value, 10) || 0; paint(); if (typeof onChange === 'function') onChange(); };
  slider.onchange = () => { markDirty(); };

  const presets = document.createElement('div');
  presets.className = 'stk-slider-presets';
  [['L', 25], ['M', 50], ['H', 75]].forEach(([t, v]) => {
    const b = document.createElement('button');
    b.type = 'button'; b.textContent = t; b.title = (field === 'power' ? 'Influence' : 'Interest') + ': ' + t;
    b.onclick = (e) => { e.stopPropagation(); setVal(v, true); };
    presets.appendChild(b);
  });

  paint();
  if (hideLabel) row.append(slider, lvl, presets);
  else row.append(label, slider, lvl, presets);
  return row;
}

function buildStakeholderCard(s) {
  const card = document.createElement('div');
  card.className = 'stk-card';
  card.dataset.id = s.id;

  /* Delete × */
  const del = document.createElement('button');
  del.className = 'stk-card-del';
  del.title = 'Delete stakeholder';
  del.innerHTML = '<svg viewBox="0 0 12 12"><path d="M2 2l8 8M10 2l-8 8"/></svg>';
  del.onclick = (e) => { e.stopPropagation(); confirmDeleteStakeholder(s.id); };
  card.appendChild(del);

  /* Head: name on left, Role + Company stacked on right (v3.0 Option A) */
  const head = document.createElement('div');
  head.className = 'stk-head';
  const name = document.createElement('div');
  name.className = 'stk-name';
  name.contentEditable = 'true';
  name.spellcheck = false;
  name.textContent = s.name || '';
  name.onblur = () => handleStakeholderNameEdit(s.id, name);
  name.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); name.blur(); } };
  head.appendChild(name);

  const metaRight = document.createElement('div');
  metaRight.className = 'stk-meta-right';

  const role = document.createElement('div');
  role.className = 'stk-role';
  role.contentEditable = 'true';
  role.spellcheck = false;
  role.textContent = s.role || '';
  role.onblur = () => handleStakeholderRoleEdit(s.id, role);
  role.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); role.blur(); } };
  metaRight.appendChild(role);

  /* v4.0: Company is an <input list="organisationList"> (was contenteditable) so it
     offers the same company suggestions as the add-stakeholder modal and the
     action owner/requestor fields. Kept borderless via .stk-organisation to sit
     flush with the Name/Role siblings. */
  const company = document.createElement('input');
  company.type = 'text';
  company.className = 'stk-organisation';
  company.spellcheck = false;
  company.setAttribute('list', 'organisationList');
  company.placeholder = 'Organisation…';
  company.value = s.organisation || '';
  company.onblur = () => handleStakeholderOrganisationEdit(s.id, company);
  company.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); company.blur(); } };
  metaRight.appendChild(company);

  /* ⚠ v14.1 (c): on the CARD this is a third CONTROL, not an array element - the scope said
     the cards shared the table's joined expression and they do not. Role and organisation
     are two separately editable siblings here, so location copies that pattern: same quiet
     styling, its own blur handler, and an entry in _STAKEHOLDER_HIGHLIGHT_SELECTORS so a
     location match highlights like a role match does. His call at the mockup: editable on
     the card, and the third stacked line 'reads quiet enough'. */
  const loc = document.createElement('input');
  loc.type = 'text';
  loc.className = 'stk-location';
  loc.spellcheck = false;
  loc.placeholder = 'Location…';
  loc.value = s.location || '';
  loc.onblur = () => handleStakeholderLocationEdit(s.id, loc);
  loc.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); loc.blur(); } };
  metaRight.appendChild(loc);

  head.appendChild(metaRight);
  card.appendChild(head);

  /* Pills row */
  const pills = document.createElement('div');
  pills.className = 'stk-pills';

  /* v3.4: category pill — a styled <select> (same idiom as the risk-card
     category/status chips). Changing it updates s.category, re-tints the pill,
     and recolours this stakeholder's grid dot. */
  const catSel = document.createElement('select');
  const _cat = STK_CATEGORIES.includes(s.category) ? s.category : 'Customer';
  catSel.className = 'stk-cat-select cat-' + _cat.toLowerCase();
  catSel.title = 'Category';
  STK_CATEGORIES.forEach(c => {
    const opt = document.createElement('option');
    opt.value = c; opt.textContent = c;
    if (c === _cat) opt.selected = true;
    catSel.appendChild(opt);
  });
  catSel.addEventListener('change', (e) => {
    const v = e.target.value;
    if (!STK_CATEGORIES.includes(v)) return;
    s.category = v;
    catSel.className = 'stk-cat-select cat-' + v.toLowerCase();
    markDirty();
    renderMendelowGrid();
  });
  const stratChip = buildStrategyChip(s);
  const onPI = () => {
    _paintStrategyChip(stratChip, stkStrategy(s));
    renderStakeholderStats();
    if (stkView() === 'matrix') _renderMatrixDots();
  };
  pills.appendChild(catSel);
  pills.appendChild(stratChip);
  pills.appendChild(buildEngagementChip(s));
  card.appendChild(pills);

  /* v5.2: inline Influence/Interest sliders — the bars double as the control
     (drag to set 0–100; live level; L/M/H presets). Replaces the L/M/H cycle chips,
     and category editing keeps re-tinting the slider fill via a card re-render. */
  const sliders = document.createElement('div');
  sliders.className = 'stk-sliders';
  sliders.appendChild(buildPISlider(s, 'power', onPI));
  sliders.appendChild(buildPISlider(s, 'interest', onPI));
  card.appendChild(sliders);

  /* v5.1 §8: engagement role / type — PMI controlled, multi-select (neutral chips). */
  card.appendChild(buildRoleTypeRow(s));

  /* Notes textarea */
  const notes = document.createElement('textarea');
  notes.className = 'stk-notes';
  notes.placeholder = STK_NOTES_PLACEHOLDER;
  notes.value = s.notes || '';
  notes.oninput = () => { /* live typing — no markDirty per keystroke */ };
  notes.onblur = () => handleStakeholderNotesEdit(s.id, notes.value);
  card.appendChild(notes);

  return card;
}

/* v10.3 (W5): buildLevelChip (the retired L/M/H chip UI) removed — superseded by the
   v5.2 0–100 power/interest sliders. Its helpers powerLevelClass/levelLabel and the
   .stk-chip/.stk-dot CSS went with it; nothing else called them. */
function buildEngagementChip(s) {
  const same = s.engagementCurrent === s.engagementTarget;
  const chip = document.createElement('span');
  chip.className = 'stk-eng' + (same ? ' stk-eng-same' : '');
  const lbl = document.createElement('span');
  lbl.className = 'stk-eng-label';
  lbl.textContent = 'Eng';
  chip.appendChild(lbl);
  const cur = document.createElement('span');
  cur.className = 'stk-eng-current';
  cur.textContent = s.engagementCurrent;
  cur.title = 'Click to change current engagement';
  cur.onclick = (e) => { e.stopPropagation(); openEngagementPopover(cur, s.id, 'current'); };
  chip.appendChild(cur);
  const arrow = document.createElement('span');
  arrow.className = 'stk-eng-arrow';
  arrow.textContent = '→';
  chip.appendChild(arrow);
  const tgt = document.createElement('span');
  tgt.className = 'stk-eng-target';
  tgt.textContent = s.engagementTarget;
  tgt.title = 'Click to change target engagement';
  tgt.onclick = (e) => { e.stopPropagation(); openEngagementPopover(tgt, s.id, 'target'); };
  chip.appendChild(tgt);
  return chip;
}

/* ── v5.1 §8: stakeholder role/type (PMI controlled, multi-select) ───────────
   Structural/functional roles ONLY — attitude stays on the engagement chip.
   Reads s.roleTypes || []; writes the array only once the user sets a role, and
   deletes it again when empty, so untouched stakeholders round-trip unchanged. */
function buildRoleTypeRow(s) {
  const row = document.createElement('div');
  row.className = 'stk-roles';
  const lbl = document.createElement('span');
  lbl.className = 'stk-roles-label';
  lbl.textContent = 'Role type';
  row.appendChild(lbl);
  renderRoleTypeChips(row, s);
  return row;
}
function renderRoleTypeChips(row, s) {
  row.querySelectorAll('.stk-role-chip, .stk-role-add, .stk-role-none').forEach(n => n.remove());
  const roles = Array.isArray(s.roleTypes) ? s.roleTypes : [];
  if (!roles.length) {
    const none = document.createElement('span');
    none.className = 'stk-role-none';
    none.textContent = 'none set';
    row.appendChild(none);
  }
  roles.forEach(r => {
    const chip = document.createElement('span');
    chip.className = 'stk-role-chip';
    chip.textContent = r;
    row.appendChild(chip);
  });
  const add = document.createElement('button');
  add.className = 'stk-role-add';
  add.textContent = roles.length ? '+' : '+ role type';
  add.title = 'Set PMI role type(s)';
  add.onclick = (e) => { e.stopPropagation(); openRoleTypePopover(add, s, row); };
  row.appendChild(add);
}
/* v17.6: each menu owns its callback, outside listener and delayed installation.
   An obsolete callback may close only its own context, never a newer person's menu. */
function _bindStakeholderMenu(kind, record, surface, pop, eventName) {
  const ctx = _stakeholderOwner(record, surface);
  if (!_stakeholderOwnerCurrent(ctx, true)) return null;
  ctx.pop = pop;
  ctx.eventName = eventName;
  ctx.outside = e => {
    if (_stkMenus[kind] === ctx && !pop.contains(e.target)) _closeStakeholderMenu(kind, ctx);
  };
  _stkMenus[kind] = ctx;
  ctx.timer = setTimeout(() => {
    ctx.timer = null;
    if (_stakeholderMenuCurrent(kind, ctx)) document.addEventListener(eventName, ctx.outside);
    else _closeStakeholderMenu(kind, ctx);
  }, 0);
  return ctx;
}
function _stakeholderMenuCurrent(kind, ctx) {
  return !!(ctx && _stkMenus[kind] === ctx && ctx.pop.isConnected
    && _stakeholderOwnerCurrent(ctx, true));
}
function _closeStakeholderMenu(kind, expected) {
  const ctx = _stkMenus[kind];
  if (!ctx || (expected && expected !== ctx)) return;
  _stkMenus[kind] = null;
  clearTimeout(ctx.timer);
  document.removeEventListener(ctx.eventName, ctx.outside);
  ctx.pop.remove();
}
function _closeStakeholderMenus() { closeRoleTypePopover(); closeEngagementPopover(); }

function openRoleTypePopover(anchor, s, row) {
  closeRoleTypePopover();
  const pop = document.createElement('div');
  pop.className = 'stk-roletype-popover';
  /* The drawer overlay is at 1000; its body-appended menu must receive pointer input. */
  if (row.closest('#stkDrawer')) pop.style.zIndex = '1001';
  /* The row survives chip/button replacement during a valid multi-toggle session. */
  const ctx = _bindStakeholderMenu('role', s, row, pop, 'mousedown');
  if (!ctx) return;
  STK_ROLE_TYPES.forEach(g => {
    const grp = document.createElement('div'); grp.className = 'srt-group';
    const gl = document.createElement('div'); gl.className = 'srt-group-label'; gl.textContent = g.group; grp.appendChild(gl);
    const opts = document.createElement('div'); opts.className = 'srt-opts';
    g.items.forEach(r => {
      const o = document.createElement('button');
      o.className = 'srt-opt';
      o.textContent = r;
      if (Array.isArray(s.roleTypes) && s.roleTypes.includes(r)) o.setAttribute('aria-pressed', 'true');
      o.onclick = (ev) => {
        ev.stopPropagation();
        if (!_stakeholderMenuCurrent('role', ctx)) { _closeStakeholderMenu('role', ctx); return; }
        if (!Array.isArray(s.roleTypes)) s.roleTypes = [];
        const i = s.roleTypes.indexOf(r);
        if (i >= 0) s.roleTypes.splice(i, 1); else s.roleTypes.push(r);
        o.setAttribute('aria-pressed', s.roleTypes.includes(r) ? 'true' : 'false');
        if (!s.roleTypes.length) delete s.roleTypes; /* never persist an empty array */
        renderRoleTypeChips(row, s);
        markDirty();
      };
      opts.appendChild(o);
    });
    grp.appendChild(opts);
    pop.appendChild(grp);
  });
  document.body.appendChild(pop);
  const r = anchor.getBoundingClientRect();
  pop.style.left = (window.scrollX + r.left) + 'px';
  pop.style.top = (window.scrollY + r.bottom + 6) + 'px';
  const pr = pop.getBoundingClientRect();
  if (pr.right > window.innerWidth - 8) pop.style.left = (window.scrollX + Math.max(8, window.innerWidth - pr.width - 8)) + 'px';
}
function closeRoleTypePopover() { _closeStakeholderMenu('role'); }
function populateRoleTypeFilter() {
  const sel = document.getElementById('stkRoleTypeFilter');
  if (!sel || sel.options.length > 1) return;
  STK_ROLE_TYPE_FLAT.forEach(r => {
    const o = document.createElement('option');
    o.value = r; o.textContent = r;
    sel.appendChild(o);
  });
  if (activeRoleTypeFilter) sel.value = activeRoleTypeFilter;
}
function setRoleTypeFilter(v) {
  activeRoleTypeFilter = v || null;
  renderStakeholders();
  if (typeof renderMendelowGrid === 'function') renderMendelowGrid();
}

/* ── Edit handlers ──────────────────────────────────────────────────── */

function handleStakeholderNameEdit(id, el) {
  const s = STAKEHOLDERS.find(x => x.id === id);
  if (!s) return;
  const newVal = (el.textContent || '').trim();
  if (newVal === s.name) return;
  s.name = newVal;
  markDirty();
  renderStakeholders();
  renderMendelowGrid();
}

function handleStakeholderRoleEdit(id, el) {
  const s = STAKEHOLDERS.find(x => x.id === id);
  if (!s) return;
  const newVal = (el.textContent || '').trim();
  if (newVal === s.role) return;
  s.role = newVal;
  markDirty();
}

/* v14.1 (c): the location a person is usually based in. The app stores the string and
   never interprets it - 'Munich', 'Singapore', 'Home, mostly' are all equally valid.
   ⚠ Blank DELETES the key (the v12 allocation-% rule, now shared with the meeting zone), so
   a stakeholder who never had one round-trips byte-identically. */
function handleStakeholderLocationEdit(id, el) {
  const s = STAKEHOLDERS.find(x => x.id === id);
  if (!s) return;
  const newVal = ((el.value !== undefined ? el.value : el.textContent) || '').trim();
  if (newVal === (s.location || '')) return;
  if (newVal) s.location = newVal; else delete s.location;
  markDirty();
}
function handleStakeholderOrganisationEdit(id, el) {
  const s = STAKEHOLDERS.find(x => x.id === id);
  if (!s) return;
  /* v4.0: company is now an <input> (.value); stay defensive in case anything
     still passes a contenteditable element. */
  const newVal = ((el.value !== undefined ? el.value : el.textContent) || '').trim();
  if (newVal === s.organisation) return;
  s.organisation = newVal;
  markDirty();
}

function handleStakeholderNotesEdit(id, value) {
  const s = STAKEHOLDERS.find(x => x.id === id);
  if (!s) return;
  const newVal = value || '';
  if (newVal === s.notes) return;
  s.notes = newVal;
  markDirty();
}

/* v10.3 (W5): cyclePower/cycleInterest removed with POWER_INTEREST_STATES — they cycled
   the retired 'low'/'medium'/'high' STRING enum, which v5.2 replaced with integer
   0–100 sliders (see stkPI). Nothing called either; leaving them would have written a
   string into a numeric field. */
/* ── Engagement popover (5-state picker) ────────────────────────────── */

function openEngagementPopover(anchorEl, stakeholderId, which) {
  closeEngagementPopover();
  const s = STAKEHOLDERS.find(x => x.id === stakeholderId);
  if (!s) return;
  const currentValue = (which === 'current') ? s.engagementCurrent : s.engagementTarget;

  const pop = document.createElement('div');
  pop.className = 'stk-eng-popover';
  const ctx = _bindStakeholderMenu('engagement', s, anchorEl.closest('.stk-eng'), pop, 'click');
  if (!ctx) return;
  ENGAGEMENT_STATES.forEach(state => {
    const btn = document.createElement('button');
    btn.className = 'stk-eng-option' + (state === currentValue ? ' is-selected' : '');
    btn.textContent = state;
    btn.onclick = (e) => {
      e.stopPropagation();
      if (!_stakeholderMenuCurrent('engagement', ctx)) { _closeStakeholderMenu('engagement', ctx); return; }
      if (which === 'current') s.engagementCurrent = state;
      else s.engagementTarget = state;
      markDirty();
      closeEngagementPopover();
      renderStakeholders();
    };
    pop.appendChild(btn);
  });

  document.body.appendChild(pop);
  const r = anchorEl.getBoundingClientRect();
  pop.style.top = (window.scrollY + r.bottom + 4) + 'px';
  pop.style.left = (window.scrollX + r.left) + 'px';
}

function closeEngagementPopover() { _closeStakeholderMenu('engagement'); }

/* ── v13.1 (U01): the v3.0 “Add stakeholder” MODAL is gone ─────────────────────
   openAddStakeholderModal had exactly ONE occurrence in the whole repo — its own
   declaration — so the overlay could never gain `.open`, and submitAddStakeholder /
   closeAddStakeholderModal / handleStakeholderOverlayClick were reachable only from
   buttons inside a modal that never opened. The live path is `+ Add stakeholder` →
   openStakeholderEditor(null, 'add'), the drawer. Its nine #s-* input ids went with it.
   Do not resurrect: the drawer is the from-scratch add path. */

/* ── Delete + undo ──────────────────────────────────────────────────── */

function confirmDeleteStakeholder(id) {
  const s = STAKEHOLDERS.find(x => x.id === id);
  if (!s) return;
  /* v10.3 (W4): escape the name. This was the ONE unescaped user string reaching
     showConfirm (which assigns innerHTML) — so a name pasted as
     `Jane Doe <jane@acme.com>` rendered as `Jane Doe `, swallowing the identifying part
     in the very dialog whose job is confirming WHICH record gets deleted. */
  const display = s.name ? '"' + _escapeHtml(s.name) + '"' : 'this stakeholder';
  showConfirm(
    'Delete stakeholder?',
    'This will remove ' + display + ' from the register.<br><br>You\'ll have 6 seconds to undo.',
    () => deleteStakeholder(id)
  );
}

function deleteStakeholder(id) {
  const idx = STAKEHOLDERS.findIndex(x => x.id === id);
  if (idx === -1) return;
  const removed = STAKEHOLDERS.splice(idx, 1)[0];
  const removedIndex = idx;
  /* ── v10.3 (W4): PRUNE THE REFERENCES TO THIS STAKEHOLDER ────────────────────────────
     Stakeholder ids ARE referenced elsewhere — RACI `workstream.assignments` and comms
     `audienceIds` both hold them by value — but nothing removed them here. Both render
     inertly (unknown ids just don't draw), which is precisely why this stayed invisible:
     combined with an id counter derived from max(remaining)+1, deleting the highest-id
     stakeholder freed that id, and the next stakeholder added inherited the deleted
     person's Accountable row and audience memberships. Captured for the undo. */
  const sweptRaci = [];   /* [{wsId, raci}] */
  WORKSTREAMS.forEach(ws => {
    if (!Array.isArray(ws.assignments)) return;
    ws.assignments.filter(a => a && a.stakeholderId === id).forEach(a => sweptRaci.push({ wsId: ws.id, raci: a.raci }));
    ws.assignments = ws.assignments.filter(a => !(a && a.stakeholderId === id));
  });
  const sweptAudience = [];   /* [commId] */
  (Array.isArray(appMeta.communications) ? appMeta.communications : []).forEach(cm => {
    if (cm && Array.isArray(cm.audienceIds) && cm.audienceIds.includes(id)) {
      sweptAudience.push(cm.id);
      cm.audienceIds = cm.audienceIds.filter(x => x !== id);
    }
  });
  markDirty();
  renderStakeholders();
  renderMendelowGrid();
  if (typeof renderRaci === 'function') renderRaci();
  if (typeof renderComms === 'function') renderComms();
  showUndoToast('Stakeholder deleted', () => {
    /* restore at original position */
    STAKEHOLDERS.splice(removedIndex, 0, removed);
    sweptRaci.forEach(({ wsId, raci }) => {          /* v10.3 (W4): put the RACI cell back */
      const ws = WORKSTREAMS.find(w => w && w.id === wsId);
      if (ws) { if (!Array.isArray(ws.assignments)) ws.assignments = []; ws.assignments.push({ stakeholderId: id, raci }); }
    });
    sweptAudience.forEach(cid => {                    /* v10.3 (W4): and the audience chip */
      const cm = (Array.isArray(appMeta.communications) ? appMeta.communications : []).find(x => x && x.id === cid);
      if (cm && Array.isArray(cm.audienceIds) && !cm.audienceIds.includes(id)) cm.audienceIds.push(id);
    });
    markDirty();
    renderStakeholders();
    renderMendelowGrid();
    if (typeof renderRaci === 'function') renderRaci();
    if (typeof renderComms === 'function') renderComms();
    showToast('Stakeholder restored', { type: 'info' });
  });
}

